/**
 * Competência — o ciclo mensal como ASSISTENTE (U2): Stepper de 7 passos com o
 * conteúdo reorganizado por etapa (nada de funcionalidade perdida; os bloqueios
 * 409/422 aparecem no passo correspondente):
 *
 *   1. Importar ponto (Dropzone CSVs Tique Taque + importações + alertas + de-para)
 *   2. Revisar variáveis (grade pré-preenchível com o mês anterior + calendário VT/VR)
 *   3. Calcular (tipo de cálculo + execuções com versão do motor)
 *   4. Conferir resultado (totais por pessoa + sócios + encargos da última run)
 *   5. Gerar arquivos (4 exports + downloads)
 *   6. Resolver divergências (importação do retorno TXT da Contmega + emissões +
 *      diff + filtros + justificar inline — 03/09/2026: importar mora aqui; o
 *      passo "Importar retorno" foi fundido nesta tela a pedido do dono)
 *   7. Fechar (estado, conferir/fechar/retificar com confirmação, snapshot)
 *
 * O passo ativo é derivado do estado real (guia, não trava — dá para navegar livre).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ActionIcon, Alert, Anchor, Badge, Button, Card, Checkbox, Collapse, Divider, FileButton, Group, Menu, Modal, SegmentedControl, Select, Stepper, Table, Text, Textarea, TextInput, Title, Tooltip } from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { IconAlertTriangle, IconDownload, IconFileUpload, IconPencil, IconPrinter, IconRefresh, IconReport, IconTrash, IconUpload } from "@tabler/icons-react";
import { api, ErroApi, urlComToken } from "../api";
import { navegar } from "../roteador";
import {
  fmtCompetencia,
  fmtMoeda,
  fmtHhMm,
  parseHhMm,
  parseMoeda,
  fmtData,
  fmtDataHora,
  ROTULO_TIPO_LANCAMENTO,
  ROTULO_TIPO_CALCULO,
  ROTULO_ESTADO,
} from "../format";
import { ErroApiAlert, OkAlert } from "../componentes/notificacoes";
import { Confirmacao, notificarOk, notificarAviso, notificarErroApi } from "../componentes/notificacoes";
import { TabelaDados, Th } from "../componentes/listas";
import { agruparDivergencias, type SubGrupoDivergencias } from "../divergencias-grupos";
import { montarExportJson, montarExportMarkdown, nomeArquivoExport } from "../divergencias-export";
import {
  lerArquivoCorrecoes,
  COR_STATUS_ACAO,
  ROTULO_STATUS_ACAO,
  ROTULO_TIPO_ACAO,
  type ArquivoCorrecoes,
  type RelatorioCorrecoes,
} from "../correcoes-import";
import { BadgeEstadoProcessamento } from "../componentes/relatorio";
import { MoedaField } from "../componentes/entradas-mantine";
import type { ProcessamentoResumo } from "./FeriasPage";
import { SecaoIaPasso7 } from "../ia-passo7";
import { SecaoDivergencias, type LoteCienciaIa } from "../divergencias-cards";
import type { ResultadoAnaliseIa } from "../ia-tipos";
import {
  BadgeStatusSync,
  BlocoReconciliacao,
  rotuloEfeitos,
  type ResultadoSyncTiqueTaque,
  type StatusTiqueTaque,
  type SyncTiqueTaque,
} from "../tiquetaque-comum";
import { PontoAvisosModal } from "../ponto-avisos-modal";

interface DetalheCompetencia {
  id: number;
  estado: string;
  tipoCalculo: string;
  motorVersion: string | null;
  perfilId?: string | null;
  perfil?: { id: string; nome: string; motorVersion: string } | null;
  _count: { lancamentos: number; runs: number };
  snapshot: { id: string; criadoEm: string; motorVersion: string } | null;
  /** 21/09/2026 — o GET devolve o model cru (criadoEm presente); opcional aqui
   *  porque mocks antigos de teste não o trazem. Ancora o wizard do passo 1. */
  criadoEm?: string;
}
interface Lancamento {
  id: string;
  funcionarioId: string;
  tipo: string;
  minutos: number | null;
  valorCentavos: number | null;
  quantidade: number | null;
  descricao: string | null;
  /** 19/08/2026 — destino da HE: "folha" (paga — default) × "banco" (crédito no banco). */
  destinoHe?: string | null;
  /** importação de ponto que criou o lançamento (null = manual). */
  pontoImportacaoId?: string | null;
  funcionario: { nome: string; matriculaDominio: number | null };
}
interface Run {
  id: string;
  motorVersion: string;
  tipoExecucao: string;
  tipoCalculo: string;
  executadoEm: string;
  _count: { itens: number };
}
/** item 3 — saldo de banco por pessoa para o pagamento em folha (passo 2).
 *  22/09/2026 — gerado/consumido no mês alimentam o resumo da folha de ponto
 *  (opcionais: respostas antigas/mocks sem eles = 0). */
interface SaldoBancoPessoa {
  funcionarioId: string;
  nome: string;
  matriculaDominio: number | null;
  saldoMinutos: number;
  /** 23/09/2026 — âncora do saldo (espelho do TT); null = Σ do ledger. */
  saldoFonte?: { competenciaId: number; dataFimIso: string } | null;
  pagoNoMesMinutos: number;
  geradoNoMesMinutos?: number;
  consumidoNoMesMinutos?: number;
}
/** 22/09/2026 — rubricas do espelho de ponto por pessoa (passo 2, bloco
 *  "Folha de ponto"): GET /competencias/:id/ponto/rubricas-espelho. */
interface RubricaEspelhoPonto {
  funcionarioId: string;
  nome: string;
  faltaInjustificadaMin: number;
  diasFaltaJson: string;
  atrasoMin: number;
  descontoDsrMin: number;
  afastamentoNaoRemuneradoMin: number;
  abonoMin: number;
  horaNoturnaReduzidaMin: number;
  noturnoNivel: string | null;
  noturnoDivergenciaMin: number;
}
interface Artefato {
  id: string;
  tipo: string;
  nomeArquivo: string;
  hashSha256: string;
  tamanhoBytes: number;
  criadoEm: string;
}
interface RetornoResumo {
  id: string;
  nomeArquivo: string;
  emissao: string;
  hashSha256: string;
  importadoEm: string;
  _count: { eventos: number; divergencias: number };
}
interface Divergencia {
  id: string;
  nivel: string;
  classe: string;
  severidade: string;
  status: string;
  alvo: string;
  descricao: string;
  calculado: number | null;
  esperado: number | null;
  delta: number | null;
  unidade: string | null;
  justificativa: string | null;
  referenciaEstudo: string | null;
}
/** E7b — NFs de serviço com retenção de INSS no fluxo da competência (passo 2,
 *  lembrete SEMPRE visível: sem NF cadastrada a divergência retencao_credito só
 *  aparecia na conferência — caso real 08/2026). */
interface NotaFiscalMes {
  id: string;
  numero: string;
  valorCentavos: number;
  descricao: string | null;
  tomador: { id: string; nome: string; aliquotaPpm: number };
}
interface RetencaoMes {
  creditoMesCentavos: number;
  acumuladoCentavos: number;
}
interface TomadorResumo {
  id: string;
  nome: string;
  aliquotaPpm: number;
  ativo: boolean; // 02/09/2026 — Select de NF nova lista SÓ ativos
}
interface Funcionario {
  id: string;
  nome: string;
  matriculaDominio: number | null;
  ativo: boolean;
  tipoVinculo: string;
  /** chave interna do Convenia (coluna A do arquivo de importação deles). */
  idConvenia?: number | null;
  /** E10e — último desligamento espelhado (grupo do seletor de variáveis, 19/08/2026). */
  desligamento?: { dataDesligamento: string; tipoTitulo: string | null } | null;
}
interface SocioResultado {
  codigoDominio: number;
  nome: string;
  eventos: { codigoDominio: number; valorCentavos: number }[];
  totais: { proventosCentavos: number; descontosCentavos: number; liquidoCentavos: number; baseIrrfCentavos: number };
  patronalCentavos: number;
}
/** Última run mensal completa (resultadoJson parseado — passo 4). */
interface ResultadoMensal {
  pessoas: {
    codigoDominio: number;
    nome: string;
    totais: { proventosCentavos: number; descontosCentavos: number; liquidoCentavos: number; fgtsCentavos: number };
  }[];
  totais: {
    proventosCentavos: number;
    descontosCentavos: number;
    liquidoCentavos: number;
    baseFgtsCentavos: number;
    fgtsCentavos: number;
    inssSeguradosCentavos: number;
    irrfCentavos: number;
    encargos: { empresaCentavos: number; terceirosCentavos: number; ratFapCentavos: number };
    socios?: { proventosCentavos: number; liquidoCentavos: number; inssSeguradosCentavos: number; irrfCentavos: number; patronalCentavos: number };
  };
  socios?: SocioResultado[];
}
interface ImportacaoPonto {
  id: string;
  tipo: string;
  nomeArquivo: string;
  /** E9-API F1: "csv" (upload) × "api" (sync Tique Taque). */
  origem: "csv" | "api";
  periodoInicio: string;
  periodoFim: string;
  alertas: string[];
  _count: { lancamentos: number; movimentosBanco: number; ajustes: number };
}
interface DeparaPonto {
  id: string;
  chaveTipo: string;
  chave: string;
  funcionario: { id: string; nome: string; matriculaDominio: number | null };
}
interface CalendarioPonto {
  pontoInicio: string;
  pontoFim: string;
  diasUteisDsr: number;
  diasDomingosFeriados: number;
  diasUteisVt: number;
}
/** M2.1 — detalhe diário do ponto (GET /competencias/:id/ponto/dias):
 *  HE/ad. noturno/banco por dia — detalhe auditável dos lançamentos. */
interface DiaPonto {
  id: string;
  funcionarioId: string;
  nome: string;
  data: string;
  extra50Min: number;
  extra100Min: number;
  adicionalNoturnoMin: number;
  bancoMinutos: number | null;
}
/** M3.1 — sugestão de reclassificação domingo/feriado → HE 100%
 *  (GET /competencias/:id/ponto/sugestoes-he100). SUGESTÃO, nunca automação
 *  (precedente 4.3): aplicar/descartar é sempre ação do gestor. */
interface SugestaoHe100 {
  id: string;
  funcionarioId: string;
  nome: string;
  data: string;
  minutos: number;
  motivo: string;
  feriadoDescricao: string | null;
  status: string; // "pendente" | "aplicada" | "descartada"
  justificativa: string | null;
  divergenteFonte: boolean;
}
/** M3.2 — fim de ciclo do banco de horas (GET /competencias/:id/banco-horas/ciclo):
 *  alerta proativo no último mês do ciclo + relatório de fechamento de ciclo
 *  por pessoa. ORIENTATIVO — a apuração do residual aguarda a decisão D7. */
interface RelatorioCicloBanco {
  competencia: number;
  parametros: { destinoResidualCiclo: string; toleranciaSaldoNegativoMinutos: number; alertaFimCicloSaldoMinutos: number };
  alertasFimCiclo: { funcionarioId: string; nome: string; fimIso: string; saldoMin: number; motivo: string; mensagem: string }[];
  fechamentos: {
    funcionarioId: string;
    nome: string;
    inicioIso: string;
    fimIso: string;
    competenciaFechamento: number;
    residualMin: number;
    saldoDerivadoCicloMin: number | null;
    avaliacao: "zerado" | "positivo" | "negativo_tolerado" | "negativo_revisar";
    mensagem: string;
  }[];
  apuracaoResidualPendenteD7: string;
}
interface Sugestao {
  funcionarioId: string;
  funcionarioNome: string;
  tipo: string;
  minutos: number | null;
  valorCentavos: number | null;
  descricao: string | null;
  jaLancado: boolean;
}
/** R3 — resumo do cancelamento (prévia do que será apagado). */
interface ResumoCancelamento {
  competencia: number;
  estado: string;
  motorVersion: string | null;
  contagens: {
    lancamentos: number;
    runs: number;
    itensCalculados: number;
    artefatos: number;
    retornos: number;
    eventosRetorno: number;
    divergencias: number;
    pontoImportacoes: number;
    pontoAjustes: number;
    movimentosBancoHoras: number;
    notasFiscais: number;
    /** 03/09/2026 — camada de IA (apagada junto no cancelamento). */
    analisesIa: number;
    mensagensIa: number;
    /** 04/09/2026 — anexos do chat de IA (apagados junto no cancelamento). */
    anexosIa: number;
    emprestimosAReativar: number;
  };
  artefatos: { tipo: string; nomeArquivo: string }[];
  notasFiscais: { numero: string; valorCentavos: number; tomador: string }[];
  importacoesPonto: { tipo: string; nomeArquivo: string }[];
  emprestimos: { id: string; descricao: string }[];
}
/** Gate para o seletor de motor da retificação (R3). */
interface GateRetificar {
  elegiveis: string[];
  sugerido: string | null;
}
/** M1.2 + M1.7 — alertas orientativos de férias da competência
 *  (GET /competencias/:id/alertas-ferias). */
interface AlertasFeriasCompetencia {
  competencia: number;
  aprovadasSemRecibo: {
    solicitacaoId: string;
    funcionarioId: string;
    nomeFuncionario: string;
    inicioGozo: string;
    fimGozo: string;
    diasGozo: number;
    status: string;
    mensagem: string;
  }[];
  concessivoUltrapassado: { solicitacaoId: string; nomeFuncionario: string; inicioGozo: string; fimConcessivo: string; mensagem: string }[];
  vencidas: { periodoId: string; nomeFuncionario: string; fimConcessivo: string; saldoDerivado: number; diasParaLimite: number }[];
}

/** UX — diagnóstico do "0 itens" (GET /competencias/:id/diagnostico-elegiveis):
 *  contagens reais de elegíveis da projeção + mensagem pronta (mesma fonte do
 *  aviso do calcular). Buscado SÓ quando a última run mensal tem 0 itens. */
interface DiagnosticoElegiveis {
  cltTotal: number;
  comContratoVigente: number;
  semMatriculaDominio: number;
  elegiveis: number;
  mensagem: string;
}

/** Competência (AAAAMM) do mês do início do gozo — o recibo de férias paga o
 *  período integral no mês do início (P1). */
function competenciaDoInicioIso(inicioIso: string): number {
  return Number(inicioIso.slice(0, 4) + inicioIso.slice(5, 7));
}

/** M1.6 (+ M1.8) — reconciliação das 3 fontes de gozo de férias
 *  (GET /competencias/:id/reconciliacao-ferias). NUNCA bloqueante. */
interface ReconciliacaoFerias {
  competencia: number;
  executada: boolean;
  motivo: string | null;
  sugestoesRecibo: {
    funcionarioId: string;
    nomeFuncionario: string;
    competencia: number;
    inicio: string;
    fim: string;
    dias: number;
    origem: string;
    mensagem: string;
  }[];
  divergencias: { total: number; abertas: number; justificadas: number; porClasse: Record<string, number> };
}

const TIPOS_ARTEFATO = [
  ["dominio_txt", "TXT Domínio"],
  ["apontamento_xlsx", "Apontamento (xlsx)"],
  ["convenia_xlsx", "Importação Convenia (xlsx)"],
  ["omie_xlsx", "Contas a pagar Omie (xlsx)"],
] as const;

const ROTULO_TIPO_CSV_PONTO: Record<string, string> = {
  horas_extras: "Horas extras",
  banco_horas: "Banco de horas",
  ajustes: "Pedidos de ajustes",
};

const PASSOS = [
  "Importar ponto",
  "Revisar variáveis",
  "Calcular",
  "Conferir resultado",
  "Gerar arquivos",
  "Resolver divergências",
  "Fechar",
];

/** 22/09/2026 — passo 2 dividido em DOIS blocos temáticos (pedido do dono):
 *  as variáveis ligadas ao ponto ficam no bloco "Folha de ponto (Tique Taque)";
 *  comissão/bônus/abono CCT (listas detalhadas, eventos 37/9025/380) no bloco
 *  "Bônus, comissões e abonos". Cada bloco tem grade + formulário próprios. */
const TIPOS_PONTO = ["he50", "he100", "adicional_noturno", "dias_descontados"];
const TIPOS_BONUS = ["comissao", "bonus", "abono_cct"];

/** hh:mm com "—" esmaecido no zero (colunas somente-leitura do passo 2). */
function CelMin({ v }: { v: number }) {
  return (
    <Text span size="sm" c={v ? undefined : "dimmed"}>
      {v ? fmtHhMm(v) : "—"}
    </Text>
  );
}

/** Faltas injustificadas em DIAS (o espelho traz a lista de datas em JSON). */
function diasDeFaltaJson(json: string): number {
  try {
    const v: unknown = JSON.parse(json);
    return Array.isArray(v) ? v.length : 0;
  } catch {
    return 0;
  }
}

/** 19/08/2026 (pedido do dono) — célula da grade de variáveis com TUDO inline
 *  (zero modal): clicar no valor vira input (máscara do tipo: hh:mm / R$ /
 *  inteiro; Enter ou blur salva, Esc cancela); a lixeira fica visível ao lado
 *  (com a confirmação de sempre); HE mostra o chip do destino SEMPRE visível (folha/banco)
 *  sem precisar clicar (clique no chip alterna) e, no modo edição, um mini
 *  segmented alterna folha×banco. Pagamento de banco é só leitura (tooltip). */
function CelulaVariavel({
  pessoaNome,
  itens,
  tipo,
  podeLancar,
  onSalvar,
  onExcluir,
  onTrocarDestino,
}: {
  pessoaNome: string;
  itens: Lancamento[];
  tipo: string;
  podeLancar: boolean;
  onSalvar: (l: Lancamento, bruto: string, descricao?: string) => Promise<void>;
  onExcluir: (l: Lancamento) => void;
  onTrocarDestino: (l: Lancamento, destino: string) => Promise<void>;
}) {
  const ehHe = tipo === "he50" || tipo === "he100" || tipo === "adicional_noturno";
  // 21/09/2026 — comissão/bônus/abono CCT são uma LISTA detalhada: cada item
  // mostra a descrição (origem) e a edição inline edita descrição + valor
  const ehDetalhado = tipo === "comissao" || tipo === "bonus" || tipo === "abono_cct";
  // a descrição padrão do formulário ("Comissão"/"Bônus"/"Abono indenizatório CCT")
  // não é origem — não polui a célula
  const descricaoVisivel = (l: Lancamento): string | null => {
    const d = (l.descricao ?? "").trim();
    if (!d) return null;
    const padrao = tipo === "comissao" ? "Comissão" : tipo === "bonus" ? "Bônus" : "Abono indenizatório CCT";
    return d === padrao ? null : l.descricao!;
  };
  const fmt = (l: Lancamento): string =>
    tipo === "comissao" || tipo === "bonus" || tipo === "abono_cct"
      ? fmtMoeda(l.valorCentavos ?? 0)
      : tipo === "dias_descontados"
        ? String(l.quantidade ?? 0)
        : fmtHhMm(l.minutos ?? 0);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [texto, setTexto] = useState("");
  const [textoDesc, setTextoDesc] = useState("");
  // ref síncrono: Enter+E blur disparam os dois — o segundo fechar é ignorado
  const editandoRef = useRef<string | null>(null);

  function iniciarEdicao(l: Lancamento) {
    editandoRef.current = l.id;
    setTexto(fmt(l));
    setTextoDesc(l.descricao ?? "");
    setEditandoId(l.id);
  }
  function fechar(l: Lancamento, salvar: boolean) {
    if (editandoRef.current !== l.id) return; // já fechado (Enter já fechou — o blur depois não repete)
    editandoRef.current = null;
    setEditandoId(null);
    const mudouValor = texto.trim() !== fmt(l);
    const mudouDesc = ehDetalhado && textoDesc.trim() !== (l.descricao ?? "");
    if (salvar && (mudouValor || mudouDesc)) void onSalvar(l, texto, ehDetalhado ? textoDesc : undefined);
  }
  /** no modo edição detalhado há DOIS inputs — o blur de um para o outro não fecha */
  function blurEdicao(l: Lancamento, e: React.FocusEvent<HTMLInputElement>) {
    const alvo = e.relatedTarget as Node | null;
    if (alvo && e.currentTarget.parentElement?.contains(alvo)) return;
    fechar(l, true);
  }

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
      {itens.map((l) => {
        const ehPagamentoBanco = (l.descricao ?? "").startsWith("Pagamento em folha");
        const emEdicao = editandoId === l.id;
        return (
          // 23/09/2026 (pedido do dono) — item em coluna: a descrição (origem)
          // fica em LINHA PRÓPRIA, sem corte/reticências; a linha do valor tem
          // valor + chip "banco" (só quando vai p/ o banco — o "folha" poluía)
          // + lixeira, sem quebrar
          <span key={l.id} style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
            {!emEdicao && !ehPagamentoBanco && ehDetalhado && descricaoVisivel(l) && (
              <Text span size="xs" c="dimmed" title={descricaoVisivel(l)!} style={{ textAlign: "right", whiteSpace: "normal", wordBreak: "break-word", maxWidth: 340 }}>
                {descricaoVisivel(l)}
              </Text>
            )}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "nowrap", justifyContent: "flex-end" }}>
            {ehPagamentoBanco ? (
              <span title="Pagamento de banco de horas — tem a baixa no banco amarrada; para corrigir, estorne na seção 'Pagamento do banco de horas' e pague de novo">
                {fmt(l)}
              </span>
            ) : emEdicao && ehDetalhado ? (
              <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
                <input
                  aria-label={`Editar descrição de ${ROTULO_TIPO_LANCAMENTO[tipo] ?? tipo} de ${pessoaNome}`}
                  value={textoDesc}
                  size={16}
                  placeholder="origem / descrição"
                  onChange={(e) => setTextoDesc(e.target.value)}
                  onBlur={(e) => blurEdicao(l, e)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") fechar(l, true);
                    if (e.key === "Escape") fechar(l, false);
                  }}
                />
                <input
                  autoFocus
                  aria-label={`Editar ${ROTULO_TIPO_LANCAMENTO[tipo] ?? tipo} de ${pessoaNome}`}
                  value={texto}
                  size={8}
                  onChange={(e) => setTexto(e.target.value)}
                  onBlur={(e) => blurEdicao(l, e)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") fechar(l, true);
                    if (e.key === "Escape") fechar(l, false);
                  }}
                />
              </span>
            ) : emEdicao ? (
              <input
                autoFocus
                aria-label={`Editar ${ROTULO_TIPO_LANCAMENTO[tipo] ?? tipo} de ${pessoaNome}`}
                value={texto}
                size={8}
                onChange={(e) => setTexto(e.target.value)}
                onBlur={() => fechar(l, true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fechar(l, true);
                  if (e.key === "Escape") fechar(l, false);
                }}
              />
            ) : podeLancar ? (
              <Button variant="subtle" size="compact-xs" onClick={() => iniciarEdicao(l)} title="Clique para editar (Enter salva, Esc cancela)">
                {fmt(l)}
              </Button>
            ) : (
              fmt(l)
            )}
            {ehHe && !ehPagamentoBanco && !emEdicao && (l.destinoHe ?? "folha") === "banco" && (
              <Badge
                size="xs"
                variant="light"
                color="teal"
                style={podeLancar ? { cursor: "pointer" } : undefined}
                title="Vai para o banco de horas (não é paga em folha) — clique para voltar a pagar em folha"
                onClick={podeLancar ? () => void onTrocarDestino(l, "folha") : undefined}
              >
                banco
              </Badge>
            )}
            {ehHe && emEdicao && (
              <SegmentedControl
                size="xs"
                aria-label={`Destino da HE de ${pessoaNome}`}
                value={l.destinoHe ?? "folha"}
                onChange={(v) => void onTrocarDestino(l, v)}
                data={[
                  { value: "folha", label: "Paga em folha" },
                  { value: "banco", label: "Banco de horas" },
                ]}
              />
            )}
            {podeLancar && !ehPagamentoBanco && (
              <ActionIcon color="red" variant="subtle" size="sm" aria-label="Excluir lançamento" title="Excluir lançamento" onClick={() => onExcluir(l)}>
                <IconTrash size={13} />
              </ActionIcon>
            )}
          </span>
          </span>
        );
      })}
    </span>
  );
}

/** 21/09/2026 (pedido do dono) — linha-resumo dos avisos da importação/sync de
 *  ponto, extraída para reuso: o passo 1 E o passo 2 mostram a mesma linha
 *  (o auto-avanço do passo escondia os avisos logo após o sync — com a linha
 *  no passo 2 o modal segue reabrível de lá). */
function LinhaAvisosPonto({ total, onAbrir }: { total: number; onAbrir: () => void }) {
  return (
    <Group gap="sm" mb="sm" wrap="wrap">
      <Text size="sm" c="yellow.8">
        ⚠ {total} aviso(s) da importação/sincronização
      </Text>
      <Button variant="light" size="xs" onClick={onAbrir}>
        Ver avisos por funcionário
      </Button>
    </Group>
  );
}

/** Linha da tabela de sincronias do Tique Taque (03/09/2026 — extraída para
 *  reuso: a tela mostra só a última corrida; o modal repete com o histórico
 *  completo, mesmas colunas). */
function LinhaSync({ s }: { s: SyncTiqueTaque }) {
  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm">{fmtDataHora(s.inicioEm)}</Text>
      </Table.Td>
      <Table.Td>
        <BadgeStatusSync status={s.status} />
        {s.erro && (
          <Text size="xs" c="red.7">{s.erro}</Text>
        )}
      </Table.Td>
      <Table.Td style={{ textAlign: "right" }}>
        <Text size="sm">{s.resumo ? rotuloEfeitos(s.resumo) : "—"}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c={s.resumo?.reconciliacaoBanco?.divergentes ? "red.7" : undefined}>
          {s.resumo
            ? s.resumo.reconciliacaoBanco
              ? `${s.resumo.reconciliacaoBanco.convergentes}/${s.resumo.reconciliacaoBanco.pessoas} convergente(s)` +
                (s.resumo.reconciliacaoBanco.divergentes > 0 ? ` · ${s.resumo.reconciliacaoBanco.divergentes} divergente(s)` : "")
              : "não reconciliado"
            : "—"}
          {s.resumo && s.resumo.alertas.length > 0 ? ` · ${s.resumo.alertas.length} alerta(s)` : ""}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}

export function CompetenciaPage({ id }: { id: number }) {
  const [detalhe, setDetalhe] = useState<DetalheCompetencia | null>(null);
  // Tarefa C (03/08/2026) — motor elegível mais recente (o próximo cálculo usa ele em competência aberta)
  const [motorSugerido, setMotorSugerido] = useState<string | null>(null);
  // 03/09/2026 (pedido do dono) — seletor de motor ao lado de "Calcular" no
  // passo 3: lista dos elegíveis do gate + a escolha do usuário. O DEFAULT é o
  // sugerido (não o pin atual): é o motor que o cálculo usaria sem escolha —
  // refletir o pin seria desonesto, porque o recálculo re-pina no mais recente.
  const [motoresElegiveis, setMotoresElegiveis] = useState<string[]>([]);
  const [motorCalculo, setMotorCalculo] = useState("");
  const [lancamentos, setLancamentos] = useState<Lancamento[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [artefatos, setArtefatos] = useState<Artefato[]>([]);
  const [retornos, setRetornos] = useState<RetornoResumo[]>([]);
  const [divergencias, setDivergencias] = useState<Divergencia[]>([]);
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [resultadoMensal, setResultadoMensal] = useState<ResultadoMensal | null>(null);
  // UX — diagnóstico do "0 itens" (só preenchido quando a última run mensal
  // calculou 0 itens; alimenta o Alert do passo 3 e o fallback do passo 4)
  const [diagElegiveis, setDiagElegiveis] = useState<DiagnosticoElegiveis | null>(null);
  const [importacoesPonto, setImportacoesPonto] = useState<ImportacaoPonto[]>([]);
  const [deparaPonto, setDeparaPonto] = useState<DeparaPonto[]>([]);
  const [calPonto, setCalPonto] = useState<CalendarioPonto | null>(null);
  // edição da data fim do período de ponto (competências ≥ 09/2026 — o início é
  // sempre derivado: dia seguinte ao fim do período anterior; somente leitura)
  const [editCalAberto, setEditCalAberto] = useState(false);
  const [editPontoFim, setEditPontoFim] = useState("");
  const [editCalErro, setEditCalErro] = useState<unknown>(null);
  const [salvandoCal, setSalvandoCal] = useState(false);
  // M2.1 — detalhe diário do ponto (grade dia × rubricas, auditável)
  const [diasPonto, setDiasPonto] = useState<DiaPonto[]>([]);
  // M3.1 — sugestões de reclassificação domingo/feriado → HE 100% (gestor decide)
  const [sugestoesHe100, setSugestoesHe100] = useState<SugestaoHe100[]>([]);
  const [descarteHe100, setDescarteHe100] = useState<{ sugestao: SugestaoHe100; justificativa: string } | null>(null);
  // M3.2 — fim de ciclo do banco de horas (alerta proativo + fechamento de ciclo)
  const [cicloBanco, setCicloBanco] = useState<RelatorioCicloBanco | null>(null);
  // E9-API F4 — sync do Tique Taque (config do token + corridas desta competência)
  const [statusTt, setStatusTt] = useState<StatusTiqueTaque | null>(null);
  const [syncsTt, setSyncsTt] = useState<SyncTiqueTaque[]>([]);
  const [sincronizandoTt, setSincronizandoTt] = useState(false);
  // 05/09/2026 — sync Convenia direto do passo 1 (pedido do dono): mesmo
  // endpoint global do batch (202 + poll) da página de integrações
  const [sincronizandoCv, setSincronizandoCv] = useState(false);
  // 21/09/2026 — wizard do passo 1 (pedido do dono): histórico de syncs do
  // Convenia para saber se o 1º passo já rodou nesta competência
  const [syncsCv, setSyncsCv] = useState<{ id: string; status: string; criadoEm: string | null }[]>([]);
  // 03/09/2026 — modal dos avisos de ponto agrupados por funcionário (pedido do dono)
  const [avisosAberto, setAvisosAberto] = useState(false);
  // 03/09/2026 — modal com o histórico COMPLETO das sincronias (a tela mostra só a última)
  const [historicoSyncsAberto, setHistoricoSyncsAberto] = useState(false);
  const [sugestoes, setSugestoes] = useState<{ origem: number | null; sugestoes: Sugestao[] } | null>(null);
  // M1.2 + M1.7 — alertas orientativos de férias do mês (nunca bloqueantes)
  const [alertasFerias, setAlertasFerias] = useState<AlertasFeriasCompetencia | null>(null);
  const [reconciliacaoFerias, setReconciliacaoFerias] = useState<ReconciliacaoFerias | null>(null);
  const [passo, setPasso] = useState(0);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState("");
  /** Navegação entre passos limpa o banner de erro/ok (antes o erro ficava
   *  preso no topo ao trocar de etapa — ex.: o 409 do fechar aparecia em
   *  TODOS os passos até outra ação). */
  function mudarPasso(p: number | ((prev: number) => number)) {
    setErro(null);
    setOk("");
    setPasso(p);
  }
  const [confirmacao, setConfirmacao] = useState<{ titulo: string; consequencia: string; rotuloConfirmar: string; acao: () => void } | null>(null);
  // R1 — Espelho da Folha (relatório interno): id do artefato aberto no visualizador
  const [relatorioAberto, setRelatorioAberto] = useState<{ artefatoId: string; nomeArquivo: string } | null>(null);
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);
  // R3 — cancelamento da competência (zona de perigo) e retificação com escolha de motor
  const [cancelarAberto, setCancelarAberto] = useState(false);
  const [resumoCancel, setResumoCancel] = useState<ResumoCancelamento | null>(null);
  const [motivoCancel, setMotivoCancel] = useState("");
  const [cancelando, setCancelando] = useState(false);
  const [retificarAberto, setRetificarAberto] = useState(false);
  const [gateRet, setGateRet] = useState<GateRetificar | null>(null);
  const [motorRet, setMotorRet] = useState("");
  const [motivoRet, setMotivoRet] = useState("");

  // formulário de lançamento — bloco 1 (Folha de ponto): SEM pré-seleção de
  // pessoa (22/09/2026, pedido do dono: o placeholder orienta a escolha)
  const [fFunc, setFFunc] = useState("");
  // 19/08/2026 — destino da HE no formulário ("folha" paga, "banco" vira crédito no banco)
  const [fDestino, setFDestino] = useState("folha");
  const [fTipo, setFTipo] = useState("he50");
  const [fMin, setFMin] = useState("");
  const [fValor, setFValor] = useState("");
  const [fQtd, setFQtd] = useState("");
  const [fDesc, setFDesc] = useState("");
  // formulário de lançamento — bloco 2 (Bônus, comissões e abonos)
  const [fFunc2, setFFunc2] = useState("");
  const [fTipo2, setFTipo2] = useState("comissao");
  const [fValor2, setFValor2] = useState("");
  const [fDesc2, setFDesc2] = useState("");
  // 22/09/2026 — o grupo "Desligados/inativos" do seletor de pessoa só entra
  // quando o usuário pede (checkbox sob o Select; um estado vale p/ os 2 formulários)
  const [incluirDesligados, setIncluirDesligados] = useState(false);
  // 22/09/2026 — "Outras rubricas do espelho de ponto" (collapse fechado por padrão)
  const [outrasAberto, setOutrasAberto] = useState(false);
  // ponto
  const [fTipoCsv, setFTipoCsv] = useState("horas_extras");
  const [fDeparaChave, setFDeparaChave] = useState("");
  const [fDeparaFunc, setFDeparaFunc] = useState("");
  // filtros de divergência — 02/09/2026 (2ª passada, pedido do dono): o padrão
  // é ABERTA (o que exige ação). As resolvidas/justificadas ficam a um clique
  // (limpar o filtro de status) — sem o padrão, a tela "inflava" com o
  // histórico resolvido a cada recálculo e parecia que nada tinha zerado.
  const [fSev, setFSev] = useState("");
  const [fStatus, setFStatus] = useState("aberta");
  // justificativa inline (modal)
  const [justId, setJustId] = useState<string | null>(null);
  const [justTexto, setJustTexto] = useState("");
  const [justRef, setJustRef] = useState("");
  // Redesenho do passo 7 (05/08/2026) — ciência em lote por sub-grupo
  const [lote, setLote] = useState<SubGrupoDivergencias | null>(null);
  const [loteTexto, setLoteTexto] = useState("");
  const [loteRef, setLoteRef] = useState("");
  // 02/09/2026 — análise de IA mais recente (enriquece os cards) + seleção da
  // ciência SUGERIDA pela IA (a aplicação é sempre um clique do dono)
  const [analiseIa, setAnaliseIa] = useState<ResultadoAnaliseIa | null>(null);
  const [selecionadasIa, setSelecionadasIa] = useState<ReadonlySet<string>>(new Set());
  // Import de correções (01/09/2026): arquivo validado → dry-run (preview) → aplicar
  const [correcoesArq, setCorrecoesArq] = useState<ArquivoCorrecoes | null>(null);
  const [correcoesArqNome, setCorrecoesArqNome] = useState("");
  const [correcoesRel, setCorrecoesRel] = useState<RelatorioCorrecoes | null>(null);
  const [correcoesOcupado, setCorrecoesOcupado] = useState(false);
  // diff entre emissões
  const [diff, setDiff] = useState<string | null>(null);
  // P1 — processamentos (férias/13º) do mês (resumo somente-leitura no passo 3)
  const [processamentos, setProcessamentos] = useState<ProcessamentoResumo[]>([]);
  // item 3 — saldos de banco para o pagamento em folha (passo 2)
  const [saldosBanco, setSaldosBanco] = useState<SaldoBancoPessoa[]>([]);
  const [bancoMin, setBancoMin] = useState<Record<string, string>>({});
  // 22/09/2026 — rubricas do espelho de ponto por pessoa (passo 2, bloco 1)
  const [espelhoPonto, setEspelhoPonto] = useState<{ total: number; rubricas: RubricaEspelhoPonto[] }>({ total: 0, rubricas: [] });
  // E7b — NFs de retenção do mês + crédito + tomadores (passo 2)
  const [nfs, setNfs] = useState<NotaFiscalMes[]>([]);
  const [retencao, setRetencao] = useState<RetencaoMes | null>(null);
  const [tomadores, setTomadores] = useState<TomadorResumo[]>([]);
  const [fNfTom, setFNfTom] = useState("");
  const [fNfNum, setFNfNum] = useState("");
  const [fNfValor, setFNfValor] = useState<number | null>(null);
  const [fNfDesc, setFNfDesc] = useState("");
  // 05/09/2026 — edição/exclusão de NF (pedido do dono: antes só cadastro)
  const [nfEdit, setNfEdit] = useState<NotaFiscalMes | null>(null);
  const [eNfTom, setENfTom] = useState("");
  const [eNfNum, setENfNum] = useState("");
  const [eNfValor, setENfValor] = useState<number | null>(null);
  const [eNfDesc, setENfDesc] = useState("");

  const podeLancar = detalhe?.estado === "aberta" || detalhe?.estado === "em_calculo" || detalhe?.estado === "retificada";
  // 09/2026+ — a data fim do período de ponto passa a ser editável (o início
  // segue automático: dia seguinte ao fim do período anterior); meses
  // anteriores ficam na regra fixa 24→23
  const calPontoEditavel = id >= 202609;
  // 03/09/2026 — avisos de TODAS as importações de ponto, flat (o modal agrupa
  // por funcionário; a avaliação da IA recebe esta mesma lista).
  // 21/09/2026 (pedido do dono) — DEDUP: o mesmo texto é persistido em até 3
  // importações do mesmo sync (HE/banco/ajustes) e inflava o contador em 3×.
  const avisosPonto = useMemo(() => [...new Set(importacoesPonto.flatMap((i) => i.alertas))], [importacoesPonto]);
  // 21/09/2026 — wizard do passo 1: o Convenia "foi feito" quando há sync
  // concluído (com ou sem erros) criado DEPOIS da abertura da competência; o
  // ponto "foi feito" quando já há importação no mês. Com os dois feitos (ou
  // com o ponto já importado pelo fluxo CSV) o 2º botão fica livre de novo.
  const conveniaFeito = useMemo(() => {
    const criadoComp = detalhe?.criadoEm ? new Date(detalhe.criadoEm).getTime() : null;
    return syncsCv.some((s) => {
      if (s.status !== "concluido" && s.status !== "concluido_com_erros") return false;
      // detalhe sem criadoEm (mocks antigos): um sync concluído basta
      if (criadoComp === null) return true;
      if (!s.criadoEm) return false;
      return new Date(s.criadoEm).getTime() >= criadoComp;
    });
  }, [syncsCv, detalhe?.criadoEm]);
  const pontoFeito = importacoesPonto.length > 0;
  const pontoLiberado = conveniaFeito || pontoFeito;
  // UX — "0 itens" auto-explicativo: última execução MENSAL sem nenhum item
  // (a lista vem em ordem desc — o find pega a mais recente)
  const ultimaRunMensal = runs.find((x) => x.tipoCalculo === "mensal");
  const runZeroItens = ultimaRunMensal !== undefined && ultimaRunMensal._count.itens === 0;
  const resultadoVazio =
    resultadoMensal !== null && resultadoMensal.pessoas.length === 0 && (resultadoMensal.socios ?? []).length === 0;

  /** 24/09/2026 (pedido do dono) — pessoas da folha sem ID Convenia: o
   *  importador deles casa pela coluna ID (a chave interna — caso real de
   *  09/2026: "Nenhum registro encontrado" com a coluna vazia). O aviso do
   *  passo 5 deixa preencher e salvar na hora. */
  const faltandoIdConvenia = useMemo(() => {
    if (!resultadoMensal) return [] as Funcionario[];
    const porMatricula = new Map(funcionarios.map((f) => [f.matriculaDominio, f]));
    return resultadoMensal.pessoas
      .map((p) => porMatricula.get(p.codigoDominio))
      .filter((f): f is Funcionario => f != null && f.idConvenia == null);
  }, [resultadoMensal, funcionarios]);
  const [idsConvenia, setIdsConvenia] = useState<Record<string, string>>({});
  async function salvarIdsConvenia() {
    await executar(async () => {
      for (const f of faltandoIdConvenia) {
        const v = (idsConvenia[f.id] ?? "").trim();
        if (!v) continue;
        const n = Number(v);
        if (!Number.isInteger(n) || n <= 0) throw new Error(`ID inválido para ${f.nome} — use o número inteiro do Convenia`);
        await api.patch(`/funcionarios/${f.id}`, { idConvenia: n });
      }
    }, "IDs do Convenia salvos — o arquivo agora casa todas as pessoas.");
  }

  async function carregar() {
    const [d, l, r, a, ret, f, imps, dep, cal, sug, procs, ttStatus, ttSyncs, alFerias, recFerias, diasPt, sugHe100, cicloBh, salBh, gateMotor, nfResp, retencaoResp, tomadoresResp, cvSyncs, espelhoPt] = await Promise.all([
      api.get<DetalheCompetencia>(`/competencias/${id}`),
      api.get<{ lancamentos: Lancamento[] }>(`/competencias/${id}/lancamentos`),
      api.get<{ runs: Run[] }>(`/competencias/${id}/runs`),
      api.get<{ artefatos: Artefato[] }>(`/competencias/${id}/artefatos`),
      api.get<{ retornos: RetornoResumo[] }>(`/competencias/${id}/retornos`),
      api.get<{ funcionarios: Funcionario[] }>("/funcionarios?tipoVinculo=clt"),
      api.get<{ importacoes: ImportacaoPonto[] }>(`/competencias/${id}/ponto/importacoes`),
      api.get<{ depara: DeparaPonto[] }>("/ponto/depara"),
      api.get<CalendarioPonto>(`/calendarios/${id}`).catch(() => null),
      api.get<{ origem: number | null; sugestoes: Sugestao[] }>(`/competencias/${id}/lancamentos-sugeridos`).catch(() => null),
      // P1 — processamentos (férias/13º) cuja competência de pagamento é este mês
      api.get<{ processamentos: ProcessamentoResumo[] }>(`/processamentos?competencia=${id}`).catch(() => ({ processamentos: [] })),
      // E9-API F4 — integração Tique Taque (falha aqui não derruba a página)
      api.get<StatusTiqueTaque>("/integracoes/tiquetaque/status").catch(() => null),
      api.get<{ syncs: SyncTiqueTaque[] }>(`/integracoes/tiquetaque/syncs?competencia=${id}`).catch(() => ({ syncs: [] })),
      // M1.2 + M1.7 — alertas orientativos de férias (falha aqui não derruba a página)
      api.get<AlertasFeriasCompetencia>(`/competencias/${id}/alertas-ferias`).catch(() => null),
      // M1.6 — reconciliação das 3 fontes de gozo (reavalia sob demanda; falha não derruba)
      api.get<ReconciliacaoFerias>(`/competencias/${id}/reconciliacao-ferias`).catch(() => null),
      // M2.1 — detalhe diário do ponto (falha aqui não derruba a página)
      api.get<{ dias: DiaPonto[] }>(`/competencias/${id}/ponto/dias`).catch(() => ({ dias: [] })),
      // M3.1 — sugestões domingo/feriado → HE 100% (falha aqui não derruba a página)
      api.get<{ sugestoes: SugestaoHe100[] }>(`/competencias/${id}/ponto/sugestoes-he100`).catch(() => ({ sugestoes: [] })),
      // M3.2 — fim de ciclo do banco de horas (falha aqui não derruba a página)
      api.get<RelatorioCicloBanco>(`/competencias/${id}/banco-horas/ciclo`).catch(() => null),
      // item 3 — saldos de banco para o pagamento em folha (falha não derruba)
      api.get<{ pessoas: SaldoBancoPessoa[] }>(`/competencias/${id}/banco-horas/saldos`).catch(() => null),
      // Tarefa C — motor elegível mais recente + 03/09/2026: lista completa dos
      // elegíveis para o seletor do passo 3 (falha aqui não derruba a página)
      api.get<{ sugerido: string | null; elegiveis?: string[] }>(`/competencias/motores-elegiveis?competencia=${id}&tipoCalculo=mensal`).catch(() => null),
      // E7b — NFs de retenção do mês + crédito + tomadores (falha não derruba a página)
      api.get<{ notasFiscais: NotaFiscalMes[] }>(`/competencias/${id}/notas-fiscais`).catch(() => null),
      api.get<RetencaoMes>(`/competencias/${id}/retencao`).catch(() => null),
      api.get<{ tomadores: TomadorResumo[] }>("/tomadores").catch(() => null),
      // 21/09/2026 — wizard do passo 1: histórico de syncs do Convenia (falha
      // aqui não derruba a página; resposta fora do shape = tratada como vazia)
      api.get<{ syncs: { id: string; status: string; criadoEm: string | null }[] }>("/integracoes/convenia/syncs").catch(() => null),
      // 22/09/2026 — rubricas do espelho de ponto por pessoa (passo 2, bloco 1;
      // falha aqui não derruba a página)
      api.get<{ total: number; rubricas: RubricaEspelhoPonto[] }>(`/competencias/${id}/ponto/rubricas-espelho`).catch(() => null),
    ]);
    setDetalhe(d);
    setLancamentos(l.lancamentos);
    setRuns(r.runs);
    setArtefatos(a.artefatos);
    setRetornos(ret.retornos);
    setFuncionarios(f.funcionarios);
    setImportacoesPonto(imps.importacoes);
    setDeparaPonto(dep.depara);
    setCalPonto(cal);
    setSugestoes(sug);
    setProcessamentos(procs.processamentos ?? []);
    setStatusTt(ttStatus);
    setSyncsTt(ttSyncs.syncs ?? []);
    setAlertasFerias(alFerias && Array.isArray(alFerias.aprovadasSemRecibo) ? alFerias : null);
    setReconciliacaoFerias(recFerias && recFerias.divergencias ? recFerias : null);
    setDiasPonto(Array.isArray(diasPt?.dias) ? diasPt.dias : []);
    setSugestoesHe100(Array.isArray(sugHe100?.sugestoes) ? sugHe100.sugestoes : []);
    setCicloBanco(cicloBh && Array.isArray(cicloBh.alertasFimCiclo) ? cicloBh : null);
    setSaldosBanco(salBh && Array.isArray(salBh.pessoas) ? salBh.pessoas : []);
    const sugMotor = gateMotor && typeof gateMotor.sugerido === "string" ? gateMotor.sugerido : null;
    setMotorSugerido(sugMotor);
    // 03/09/2026 — elegíveis do gate alimentam o seletor do passo 3; a escolha
    // do usuário é preservada entre recargas enquanto seguir elegível
    const elegiveis = gateMotor && Array.isArray(gateMotor.elegiveis) ? gateMotor.elegiveis : [];
    setMotoresElegiveis(elegiveis);
    setMotorCalculo((atual) => (atual && elegiveis.includes(atual) ? atual : (sugMotor ?? "")));
    const nfsMes = nfResp && Array.isArray(nfResp.notasFiscais) ? nfResp.notasFiscais : [];
    setNfs(nfsMes);
    setRetencao(retencaoResp && typeof retencaoResp.creditoMesCentavos === "number" ? retencaoResp : null);
    const toms = tomadoresResp && Array.isArray(tomadoresResp.tomadores) ? tomadoresResp.tomadores : [];
    setTomadores(toms);
    setSyncsCv(
      cvSyncs && Array.isArray(cvSyncs.syncs)
        ? cvSyncs.syncs.filter((s) => s && typeof s.status === "string")
        : [],
    );
    setEspelhoPonto(
      espelhoPt && Array.isArray(espelhoPt.rubricas)
        ? { total: typeof espelhoPt.total === "number" ? espelhoPt.total : espelhoPt.rubricas.length, rubricas: espelhoPt.rubricas }
        : { total: 0, rubricas: [] },
    );
    // seleção inicial = primeiro ATIVO (inativos não entram em NF nova — 02/09/2026)
    if (!fNfTom && toms.some((t) => t.ativo)) setFNfTom(toms.find((t) => t.ativo)!.id);
    // 22/09/2026 — SEM pré-seleção de pessoa nos formulários do passo 2
    // (pedido do dono): o placeholder "Selecione a pessoa" orienta a escolha
    if (!fDeparaFunc && f.funcionarios.length > 0) setFDeparaFunc(f.funcionarios[0]!.id);
    // última run mensal completa (passo 4 — totais por pessoa + sócios)
    const ultimaMensal = r.runs.find((x) => x.tipoCalculo === "mensal");
    if (ultimaMensal) {
      const runCompleta = await api.get<{ resultado: ResultadoMensal }>(`/runs/${ultimaMensal.id}`);
      setResultadoMensal(runCompleta.resultado);
    } else {
      setResultadoMensal(null);
    }
    // UX — "0 itens": busca o diagnóstico com as contagens reais SÓ quando a
    // última execução mensal calculou 0 itens (falha aqui não derruba a página
    // — o Alert do passo 3 tem mensagem de fallback)
    if (ultimaMensal && ultimaMensal._count.itens === 0) {
      const diag = await api.get<DiagnosticoElegiveis>(`/competencias/${id}/diagnostico-elegiveis`).catch(() => null);
      setDiagElegiveis(diag && typeof diag.mensagem === "string" ? diag : null);
    } else {
      setDiagElegiveis(null);
    }
  }

  async function carregarDivergencias() {
    const q = new URLSearchParams();
    if (fSev) q.set("severidade", fSev);
    if (fStatus) q.set("status", fStatus);
    const r = await api.get<{ divergencias: Divergencia[] }>(`/competencias/${id}/divergencias?${q}`);
    setDivergencias(r.divergencias);
  }

  /** Export das divergências (01/09/2026, pedido do dono): gera .md (legível)
   *  ou .json (machine-readable, schemaVersao) para enviar ao agente de IA.
   *  SEMPRE sem os filtros da tela — busca TODAS as divergências da
   *  competência (GET sem query). 100% client-side (Blob + download). */
  async function exportarDivergencias(formato: "md" | "json") {
    try {
      setErro(null);
      const r = await api.get<{ divergencias: Divergencia[] }>(`/competencias/${id}/divergencias`);
      const agora = new Date();
      const ctx = {
        competenciaId: id,
        competenciaRotulo: fmtCompetencia(id),
        geradoEmIso: agora.toISOString(),
        retornoId: retornos.length > 0 ? retornos[retornos.length - 1]!.id : null, // a lista vem asc — a mais recente é a última
        runId: runs.find((x) => x.tipoCalculo === "mensal")?.id ?? null,
        origemUrl: window.location.origin, // 02/09/2026, pedido do dono: saber de onde o export saiu (VPS x local)
      };
      const conteudo =
        formato === "json" ? JSON.stringify(montarExportJson(r.divergencias, ctx), null, 2) : montarExportMarkdown(r.divergencias, ctx);
      const blob = new Blob([conteudo], { type: formato === "json" ? "application/json;charset=utf-8" : "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nomeArquivoExport(id, formato, agora);
      a.click();
      URL.revokeObjectURL(url);
      notificarOk(`Export ${formato.toUpperCase()} gerado (TODAS as ${r.divergencias.length} divergências, sem filtro): ${a.download}`);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  /** Sugestão da IA (03/09/2026): a correção proposta vira um ArquivoCorrecoes
   *  em memória (sem arquivo no disco) e entra no MESMO fluxo do import —
   *  dry-run com prévia no modal; aplicar continua sendo decisão do dono. */
  async function carregarCorrecoesIa(arq: ArquivoCorrecoes, nome: string) {
    try {
      setErro(null);
      setCorrecoesArq(arq);
      setCorrecoesArqNome(nome);
      setCorrecoesOcupado(true);
      const rel = await api.post<RelatorioCorrecoes>(`/competencias/${id}/correcoes/importar`, arq);
      setCorrecoesRel(rel);
    } catch (e) {
      setCorrecoesArq(null);
      setCorrecoesRel(null);
      setErro(e);
      notificarErroApi(e);
    } finally {
      setCorrecoesOcupado(false);
    }
  }

  /** Import de correções (01/09/2026, pedido do dono): o arquivo JSON que o
   *  agente de IA devolve a partir do export de divergências. A página SEMPRE
   *  chama o dry-run primeiro (preview no modal — nada é escrito); o botão
   *  "Aplicar" refaz a chamada com aplicar:true. */
  async function escolherArquivoCorrecoes(file: File | null) {
    if (!file) return;
    try {
      setErro(null);
      const arq = await lerArquivoCorrecoes(file);
      setCorrecoesArq(arq);
      setCorrecoesArqNome(file.name);
      setCorrecoesOcupado(true);
      const rel = await api.post<RelatorioCorrecoes>(`/competencias/${id}/correcoes/importar`, arq);
      setCorrecoesRel(rel);
    } catch (e) {
      setCorrecoesArq(null);
      setCorrecoesRel(null);
      setErro(e);
      notificarErroApi(e);
    } finally {
      setCorrecoesOcupado(false);
    }
  }

  async function aplicarCorrecoes() {
    if (!correcoesArq) return;
    try {
      setErro(null);
      setCorrecoesOcupado(true);
      const rel = await api.post<RelatorioCorrecoes>(`/competencias/${id}/correcoes/importar`, { ...correcoesArq, aplicar: true });
      setCorrecoesRel(rel);
      notificarOk(
        `Import aplicado: ${rel.totais.validas} ação(ões) aplicada(s)` +
          (rel.totais.falhas > 0 ? `, ${rel.totais.falhas} falha(s)` : "") +
          (rel.totais.naoSuportadas > 0 ? `, ${rel.totais.naoSuportadas} não suportada(s)` : "") +
          (rel.totais.requerRecalculo > 0 ? " — recalcule a competência no passo 3." : "."),
      );
      await carregarDivergencias().catch(() => undefined);
      await carregar().catch(() => undefined); // lançamentos/estado podem ter mudado
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    } finally {
      setCorrecoesOcupado(false);
    }
  }

  function fecharCorrecoes() {
    setCorrecoesArq(null);
    setCorrecoesArqNome("");
    setCorrecoesRel(null);
  }

  async function executar<T = unknown>(acao: () => Promise<T>, mensagem: string): Promise<T | undefined> {
    try {
      setErro(null);
      setOk("");
      const r = await acao();
      setOk(mensagem);
      notificarOk(mensagem);
      await carregar();
      await carregarDivergencias().catch(() => undefined);
      return r;
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
      return undefined;
    }
  }

  /** Passo 8: "Fechar competência" já CONFERE antes quando o mês ainda está
   *  em_calculo (o ciclo exige em_calculo → conferida → fechada — sem isso o
   *  clique direto no fechar tomava o 409 de transição). Se o conferir falha
   *  (sem retorno importado / bloqueantes abertas), o erro dele aparece no
   *  passo e a confirmação de fechar nem abre. */
  async function fecharComConferencia() {
    if (detalhe?.estado === "em_calculo") {
      const r = await executar(
        () => api.post(`/competencias/${id}/conferir`),
        "Competência conferida — sem bloqueantes abertas. Confirme o fechamento.",
      );
      if (r === undefined) return;
    }
    setConfirmacao({
      titulo: `Fechar a competência ${fmtCompetencia(id)}?`,
      consequencia:
        "A competência ficará travada: não dá mais para lançar, calcular nem gerar arquivos nela. " +
        "O resultado vira um retrato permanente (snapshot). Se precisar corrigir algo depois, " +
        "a única via será a retificação — a versão original continua intacta.",
      rotuloConfirmar: "Fechar competência",
      acao: () => void executar(() => api.post(`/competencias/${id}/fechar`), "Competência fechada — snapshot gravado."),
    });
  }

  /** Calcula a folha mensal e mostra os avisos da execução em toast amarelo
   *  (ex.: o diagnóstico do "0 itens" — a execução não calculou nada porque a
   *  projeção não encontrou elegíveis).
   *  03/09/2026: envia motorVersion SEMPRE que houver seleção no passo 3 (o
   *  backend valida contra os elegíveis do gate; sem seleção, comportamento de
   *  sempre — re-pin automático no mais recente). */
  async function calcularFolhaMensal() {
    const r = await executar(
      () => api.post<{ avisos?: string[] }>(`/competencias/${id}/calcular`, motorCalculo ? { motorVersion: motorCalculo } : {}),
      "Cálculo executado e run gravada (Folha mensal).",
    );
    for (const a of r?.avisos ?? []) notificarAviso(a);
  }

  /** 03/09/2026 (pedido do dono): o recálculo ZERA a lista de divergências e a
   *  refaz do zero (ciências dadas são perdidas — o histórico fica na auditoria;
   *  análises de IA são apagadas junto). A confirmação deixa isso explícito ANTES
   *  do clique valer — sem surpresa. Quando o usuário escolheu no passo 3 um
   *  motor diferente do pin atual, a frase registra a escolha ("(motor X)"). */
  function confirmarCalculoFolhaMensal() {
    const motorEscolhidoDifere = motorCalculo !== "" && motorCalculo !== detalhe?.motorVersion;
    setConfirmacao({
      titulo: `Recalcular a folha de ${fmtCompetencia(id)}?`,
      consequencia:
        "O recálculo APAGA toda a lista de divergências da competência — inclusive ciências e justificativas já dadas — " +
        "e refaz a análise do zero contra o retorno da Contmega importado (as análises de IA também são apagadas). " +
        "O que reaparecer na lista nova é divergência REAL do cálculo atual; o histórico do que foi apagado fica na auditoria." +
        (motorEscolhidoDifere ? ` A execução usará a versão escolhida no passo 3 (motor ${motorCalculo}).` : ""),
      rotuloConfirmar: "Recalcular e zerar a lista",
      acao: () => void calcularFolhaMensal(),
    });
  }

  // R3 — cancelamento: abre o modal já com o resumo real do que será apagado
  async function abrirCancelar() {
    try {
      setErro(null);
      const r = await api.get<ResumoCancelamento>(`/competencias/${id}/resumo-cancelamento`);
      setResumoCancel(r);
      setMotivoCancel("");
      setCancelarAberto(true);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  async function confirmarCancelar() {
    setCancelando(true);
    try {
      await api.post(`/competencias/${id}/cancelar`, { motivo: motivoCancel.trim() });
      notificarOk(`Competência ${fmtCompetencia(id)} cancelada — o mês pode ser aberto de novo (até com outro motor).`);
      setCancelarAberto(false);
      navegar("/competencias");
    } catch (e) {
      notificarErroApi(e, "Não deu para cancelar");
      setErro(e);
    } finally {
      setCancelando(false);
    }
  }

  // R3 — retificação com escolha de motor (elegíveis do gate; pin marcado como atual)
  async function abrirRetificar() {
    try {
      setErro(null);
      const g = await api.get<GateRetificar>(`/competencias/motores-elegiveis?competencia=${id}&tipoCalculo=${detalhe?.tipoCalculo ?? "mensal"}`);
      setGateRet(g);
      setMotorRet(detalhe?.motorVersion ?? g.sugerido ?? "");
      setMotivoRet("");
      setRetificarAberto(true);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  async function confirmarRetificar() {
    const pin = detalhe?.motorVersion ?? "motor@2026.1";
    try {
      setErro(null);
      setOk("");
      await api.post(`/competencias/${id}/retificar`, {
        motorVersion: motorRet && motorRet !== pin ? motorRet : undefined,
        motivo: motivoRet.trim() || undefined,
      });
      const msg = "Retificação gravada como nova execução marcada.";
      setOk(msg);
      notificarOk(msg);
      await carregar();
      await carregarDivergencias().catch(() => undefined);
      setRetificarAberto(false);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  useEffect(() => {
    carregar().catch(setErro);
    carregarDivergencias().catch(setErro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    carregarDivergencias().catch(setErro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fSev, fStatus]);

  // passo ativo derivado do estado real (guia, não trava)
  // 03/09/2026 — passo "Importar retorno" fundido dentro de "Resolver
  // divergências" (a importação mora na mesma tela): índices após o 4 caíram 1.
  const passoAtual = useMemo(() => {
    if (!detalhe) return 0;
    if (detalhe.estado === "fechada") return PASSOS.length - 1;
    if (detalhe.estado === "conferida") return 6;
    if (retornos.length > 0) return 5;
    if (artefatos.length > 0) return 5;
    if (runs.length > 0) return 4;
    if (lancamentos.length > 0 || importacoesPonto.length > 0) return 2;
    return 0;
  }, [detalhe, retornos, artefatos, runs, lancamentos, importacoesPonto]);

  useEffect(() => {
    setPasso(passoAtual);
  }, [passoAtual]);

  /** 19/08/2026 (pedido do dono) — seletor de pessoa do passo 2: VIGENTES na
   *  competência primeiro (ativo e, se desligado, só depois do mês dela),
   *  desligados sinalizados depois; alfabético dentro de cada grupo. A busca
   *  por nome fica no Select (searchable).
   *  22/09/2026 (pedido do dono) — o grupo "Desligados/inativos" só entra quando
   *  `incluirDesligados` é pedido (checkbox sob o Select); sem pré-seleção. */
  function ehVigenteNaCompetencia(f: Funcionario): boolean {
    const fimMes = new Date(Date.UTC(Math.floor(id / 100), id % 100, 0)); // último dia do mês da competência
    if (!f.ativo) return false;
    if (f.desligamento && new Date(f.desligamento.dataDesligamento + "T00:00:00Z") <= fimMes) return false;
    return true;
  }
  function dadosSeletorPessoa(comDesligados: boolean): ({ group: string; items: { value: string; label: string }[] })[] {
    const ordena = (a: Funcionario, b: Funcionario) => a.nome.localeCompare(b.nome, "pt-BR");
    const vigentes = funcionarios.filter(ehVigenteNaCompetencia).sort(ordena).map((f) => ({ value: f.id, label: f.nome }));
    const grupos = [{ group: "Vigentes na competência", items: vigentes }];
    if (comDesligados) {
      const desligados = funcionarios
        .filter((f) => !ehVigenteNaCompetencia(f))
        .sort(ordena)
        .map((f) => ({
          value: f.id,
          label: f.nome + (f.desligamento ? " — desligado " + fmtData(f.desligamento.dataDesligamento) : " — inativo"),
        }));
      if (desligados.length > 0) grupos.push({ group: "Desligados/inativos", items: desligados });
    }
    return grupos;
  }
  /** 22/09/2026 — desmarcar "Incluir desligados/inativos" com um desligado
   *  selecionado em algum dos dois formulários limpa a seleção dele. */
  function alternarIncluirDesligados(v: boolean) {
    setIncluirDesligados(v);
    if (!v) {
      if (fFunc && !funcionarios.some((f) => f.id === fFunc && ehVigenteNaCompetencia(f))) setFFunc("");
      if (fFunc2 && !funcionarios.some((f) => f.id === fFunc2 && ehVigenteNaCompetencia(f))) setFFunc2("");
    }
  }
  // 22/09/2026 — grade do bloco 2 (Bônus, comissões e abonos): só as colunas
  // comissao/bonus/abono_cct, mesma célula detalhada (descrição/origem + valor)
  const gradeBonus = useMemo(() => {
    const porPessoa = new Map<string, { nome: string; funcionarioId: string; itens: Record<string, Lancamento[]> }>();
    for (const l of lancamentos) {
      if (!TIPOS_BONUS.includes(l.tipo)) continue;
      const chave = String(l.funcionario.matriculaDominio ?? 0);
      const g = porPessoa.get(chave) ?? { nome: l.funcionario.nome, funcionarioId: l.funcionarioId, itens: {} };
      (g.itens[l.tipo] ??= []).push(l);
      porPessoa.set(chave, g);
    }
    return { tipos: TIPOS_BONUS, linhas: [...porPessoa.entries()].sort((a, b) => a[1].nome.localeCompare(b[1].nome, "pt-BR")) };
  }, [lancamentos]);
  // 22/09/2026 — resumo da folha de ponto (bloco 1): uma linha por pessoa, na
  // UNIÃO de quem tem lançamentos de ponto, banco ≠ 0 ou rubrica de espelho ≠ 0.
  // As 4 primeiras colunas são editáveis (CelulaVariavel); as de banco são
  // somente-leitura. "Total HE pagas" = HE 50% + HE 100% PAGAS em folha (destino
  // "banco" NÃO entra — não é paga) + a HE paga do banco no mês.
  const resumoPonto = useMemo(() => {
    const porPessoa = new Map<string, { nome: string; itens: Record<string, Lancamento[]> }>();
    for (const l of lancamentos) {
      if (!TIPOS_PONTO.includes(l.tipo)) continue;
      const g = porPessoa.get(l.funcionarioId) ?? { nome: l.funcionario.nome, itens: {} };
      (g.itens[l.tipo] ??= []).push(l);
      porPessoa.set(l.funcionarioId, g);
    }
    const bancoPorId = new Map(saldosBanco.map((p) => [p.funcionarioId, p]));
    const espelhoPorId = new Map(espelhoPonto.rubricas.map((r) => [r.funcionarioId, r]));
    const ids = new Set<string>(porPessoa.keys());
    for (const p of saldosBanco) {
      if ((p.saldoMinutos ?? 0) !== 0 || (p.pagoNoMesMinutos ?? 0) !== 0 || (p.geradoNoMesMinutos ?? 0) !== 0 || (p.consumidoNoMesMinutos ?? 0) !== 0) ids.add(p.funcionarioId);
    }
    for (const r of espelhoPonto.rubricas) {
      if (
        (r.faltaInjustificadaMin ?? 0) !== 0 ||
        (r.atrasoMin ?? 0) !== 0 ||
        (r.descontoDsrMin ?? 0) !== 0 ||
        (r.afastamentoNaoRemuneradoMin ?? 0) !== 0 ||
        (r.abonoMin ?? 0) !== 0 ||
        (r.horaNoturnaReduzidaMin ?? 0) !== 0
      ) {
        ids.add(r.funcionarioId);
      }
    }
    const somaFolha = (itens: Lancamento[] | undefined): number =>
      (itens ?? []).filter((l) => (l.destinoHe ?? "folha") !== "banco").reduce((s, l) => s + (l.minutos ?? 0), 0);
    return [...ids]
      .map((fid) => {
        const g = porPessoa.get(fid);
        const b = bancoPorId.get(fid);
        const pagoNoMes = b?.pagoNoMesMinutos ?? 0;
        return {
          funcionarioId: fid,
          nome: g?.nome ?? b?.nome ?? espelhoPorId.get(fid)?.nome ?? fid,
          itens: g?.itens ?? {},
          pagoNoMes,
          totalPagas: somaFolha(g?.itens.he50) + somaFolha(g?.itens.he100) + pagoNoMes,
          gerado: b?.geradoNoMesMinutos ?? 0,
          consumido: b?.consumidoNoMesMinutos ?? 0,
          saldo: b?.saldoMinutos ?? 0,
          saldoFonte: b?.saldoFonte ?? null,
        };
      })
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [lancamentos, saldosBanco, espelhoPonto]);

  /** 19/08/2026 — marcador HE paga × banco por lançamento (editor da célula). */
  async function mudarDestinoLancamento(l: Lancamento, destino: string) {
    if ((l.destinoHe ?? "folha") === destino) return;
    await executar(async () => {
      await api.put("/lancamentos/" + l.id, { destinoHe: destino });
    }, destino === "banco" ? "HE vai para o banco de horas — não entra como paga na folha." : "HE volta a ser paga em folha — o crédito saiu do banco.");
  }

  /** 19/08/2026 — salvar da edição INLINE da célula (sem modal): parse pela
   *  máscara do tipo; formato inválido = aviso amarelo e NADA é enviado.
   *  21/09/2026 — comissão/bônus/abono CCT também editam a descrição (origem). */
  async function salvarInlineLancamento(l: Lancamento, bruto: string, descricao?: string) {
    let corpo: Record<string, unknown>;
    try {
      if (l.tipo === "comissao" || l.tipo === "bonus" || l.tipo === "abono_cct") {
        corpo = { valorCentavos: parseMoeda(bruto || "0") };
        // 21/09/2026 — a edição inline detalhada também edita a descrição (origem)
        if (descricao !== undefined && descricao.trim() !== (l.descricao ?? "")) corpo.descricao = descricao.trim() || null;
      } else if (l.tipo === "dias_descontados") {
        const q = Number(bruto || "0");
        if (!Number.isInteger(q)) throw new Error("inteiro");
        corpo = { quantidade: q };
      } else corpo = { minutos: parseHhMm(bruto || "0:00") };
    } catch {
      notificarAviso("Formato não reconhecido — HE/AN em hh:mm (ex.: 1:30), valores em 1.234,56, dias em inteiro. Nada foi alterado.");
      return;
    }
    await executar(async () => {
      await api.put(`/lancamentos/${l.id}`, corpo);
    }, "Lançamento atualizado.");
  }

  /** 19/08/2026 — exclusão de lançamento pede confirmação (a lixeira da grade abre
   *  o diálogo); importado do ponto avisa: a importação é idempotente por hash,
   *  ele só volta se a importação for excluída e o arquivo reimportado. */
  function pedirExclusaoLancamento(l: Lancamento) {
    const importado = l.pontoImportacaoId != null;
    setConfirmacao({
      titulo: "Excluir este lançamento?",
      consequencia:
        "O lançamento some da folha desta competência (recalcule no passo 3). " +
        ((l.destinoHe ?? "folha") === "banco" ? "O crédito dele no banco de horas sai junto. " : "") +
        (importado
          ? "Ele veio da importação de ponto: reimportar o MESMO arquivo não o recria (a importação é idempotente) — ele só volta se a importação for excluída e o arquivo importado de novo."
          : "Esta ação fica registrada na auditoria."),
      rotuloConfirmar: "Excluir",
      acao: () => void excluirLancamento(l),
    });
  }

  async function excluirLancamento(l: Lancamento) {
    const r = await executar(async () => {
      await api.del(`/lancamentos/${l.id}`);
    }, "Lançamento excluído.");
    // o DELETE avisa quando o lançamento era da importação de ponto (idempotência)
    for (const a of (r as { avisos?: string[] } | undefined)?.avisos ?? []) notificarAviso(a);
  }

  /** 22/09/2026 — envio do "Novo lançamento" de cada bloco do passo 2:
   *  bloco 1 = Folha de ponto (he50/he100/adicional_noturno/dias_descontados);
   *  bloco 2 = Bônus, comissões e abonos (comissao/bonus/abono_cct). */
  async function enviarLancamento(bloco: 1 | 2) {
    const func = bloco === 1 ? fFunc : fFunc2;
    const tipo = bloco === 1 ? fTipo : fTipo2;
    if (!func) return;
    await executar(async () => {
      const corpo: Record<string, unknown> = { funcionarioId: func, tipo };
      if (tipo === "comissao" || tipo === "bonus" || tipo === "abono_cct") {
        const valor = bloco === 1 ? fValor : fValor2;
        const desc = bloco === 1 ? fDesc : fDesc2;
        corpo.valorCentavos = parseMoeda(valor || "0");
        corpo.descricao = desc || (tipo === "bonus" ? "Bônus" : tipo === "abono_cct" ? "Abono indenizatório CCT" : "Comissão");
      } else if (tipo === "dias_descontados") {
        corpo.quantidade = Number(fQtd || 0);
        corpo.descricao = fDesc || null;
      } else {
        corpo.minutos = parseHhMm(fMin || "0:00");
        corpo.descricao = fDesc || null;
        corpo.destinoHe = fDestino;
      }
      await api.post(`/competencias/${id}/lancamentos`, corpo);
      setFMin("");
      setFValor("");
      setFQtd("");
      setFDesc("");
      setFDestino("folha");
      setFValor2("");
      setFDesc2("");
    }, "Lançamento registrado.");
  }

  /** item 3 — paga horas do banco em folha (lançamento he50 + baixa no ledger). */
  async function pagarBanco(funcionarioId: string) {
    const minutos = parseHhMm(bancoMin[funcionarioId] || "0:00");
    if (minutos <= 0) return;
    await executar(async () => {
      await api.post(`/competencias/${id}/banco-horas/pagar`, { funcionarioId, minutos });
      setBancoMin((m) => ({ ...m, [funcionarioId]: "" }));
    }, "Pagamento de banco lançado (HE 50%) — o saldo já caiu e as horas entram no próximo cálculo.");
  }

  /** item 3 — estorna um pagamento de banco (devolve as horas ao banco). */
  async function estornarBanco(lancamentoId: string) {
    await executar(async () => {
      await api.del(`/competencias/${id}/banco-horas/pagar/${lancamentoId}`);
    }, "Pagamento estornado — as horas voltaram para o banco.");
  }

  /** E7b — cadastro de NF de serviço com retenção de INSS (o crédito de 11%
   *  compensa a GPS e é conferido contra o retorno no passo 7). 05/09/2026:
   *  edição/exclusão por NF (pedido do dono) — o crédito deriva das NFs, então
   *  muda na hora; a conferência de divergências é refeita no próximo
   *  recálculo/reimport (aviso no toast). */
  async function cadastrarNf() {
    const numero = fNfNum.trim();
    if (!fNfTom || !numero || fNfValor === null) return;
    await executar(async () => {
      await api.post(`/competencias/${id}/notas-fiscais`, {
        tomadorId: fNfTom,
        numero,
        valorCentavos: fNfValor,
        descricao: fNfDesc.trim() || undefined,
      });
      setFNfNum("");
      setFNfValor(null);
      setFNfDesc("");
    }, `NF ${numero} cadastrada — o crédito de retenção já entra na conferência (passo 6).`);
  }

  /** Abre o modal de edição com os valores atuais da NF. */
  function abrirEdicaoNf(nf: NotaFiscalMes) {
    setNfEdit(nf);
    setENfTom(nf.tomador.id);
    setENfNum(nf.numero);
    setENfValor(nf.valorCentavos);
    setENfDesc(nf.descricao ?? "");
  }

  async function salvarEdicaoNf() {
    if (!nfEdit) return;
    const numero = eNfNum.trim();
    if (!eNfTom || !numero || eNfValor === null) return;
    const r = await executar(async () => {
      const r2 = await api.patch<{ ok?: boolean }>(`/notas-fiscais/${nfEdit.id}`, {
        tomadorId: eNfTom,
        numero,
        valorCentavos: eNfValor,
        descricao: eNfDesc.trim() || null,
      });
      return r2;
    }, `NF ${numero} atualizada — a conferência de divergências será refeita no próximo recálculo/reimport.`);
    if (r !== undefined) setNfEdit(null);
  }

  function excluirNf(nf: NotaFiscalMes) {
    setConfirmacao({
      titulo: `Excluir a NF ${nf.numero}?`,
      consequencia:
        `A nota de ${nf.tomador.nome} (${fmtMoeda(nf.valorCentavos)}) sai da competência e o crédito de retenção muda na hora. ` +
        "A conferência de divergências será refeita no próximo recálculo/reimport.",
      rotuloConfirmar: "Excluir NF",
      acao: () => void executar(() => api.del(`/notas-fiscais/${nf.id}`), `NF ${nf.numero} excluída.`),
    });
  }

  /** Abre o modal da data fim do ponto com o valor atual pré-preenchido. */
  function abrirEdicaoPontoFim() {
    if (!calPonto) return;
    setEditPontoFim(calPonto.pontoFim.slice(0, 10));
    setEditCalErro(null);
    setEditCalAberto(true);
  }

  /** PATCH /calendarios/:id — a API devolve a linha atualizada + `avisos` (cada
   *  um vira toast amarelo). Erro aparece DENTRO do modal, sem fechá-lo. */
  async function salvarPontoFim() {
    if (!editPontoFim || salvandoCal) return;
    setSalvandoCal(true);
    setEditCalErro(null);
    try {
      const r = await api.patch<{ avisos?: string[] }>(`/calendarios/${id}`, { pontoFim: editPontoFim });
      setEditCalAberto(false);
      notificarOk(`Data fim do período de ponto atualizada para ${fmtData(editPontoFim)} — o início da próxima competência é recalculado sozinho.`);
      for (const a of r?.avisos ?? []) notificarAviso(a);
      await carregar();
    } catch (e) {
      setEditCalErro(e);
    } finally {
      setSalvandoCal(false);
    }
  }

  async function aplicarSugestoes() {
    if (!sugestoes) return;
    const pendentes = sugestoes.sugestoes.filter((s) => !s.jaLancado);
    if (pendentes.length === 0) return;
    await executar(async () => {
      for (const s of pendentes) {
        await api.post(`/competencias/${id}/lancamentos`, {
          funcionarioId: s.funcionarioId,
          tipo: s.tipo,
          minutos: s.minutos ?? undefined,
          valorCentavos: s.valorCentavos ?? undefined,
          descricao: s.descricao ? `${s.descricao} (sugestão de ${fmtCompetencia(sugestoes.origem!)})` : `Sugestão de ${fmtCompetencia(sugestoes.origem!)}`,
        });
      }
    }, `${pendentes.length} sugestão(ões) aplicada(s) como lançamentos — revise o delta do mês.`);
  }

  /** R1 — gera (idempotente) e abre o Espelho da Folha no visualizador. */
  async function abrirRelatorioInterno() {
    setGerandoRelatorio(true);
    try {
      setErro(null);
      const r = await api.post<{ id: string; nomeArquivo: string; geradoAgora: boolean }>(
        `/competencias/${id}/exportar`,
        { tipo: "relatorio_interno" },
      );
      setRelatorioAberto({ artefatoId: r.id, nomeArquivo: r.nomeArquivo });
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    } finally {
      setGerandoRelatorio(false);
    }
  }

  /** 21/09/2026 (pedido do dono) — o POST de importação de ponto (CSV ou sync
   *  da API) devolve 409 quando o conteúdo novo diverge do já importado: em
   *  vez do erro cru, a UI oferece a SOBRESCRITA com confirmação explícita. */
  function erro409JaExisteImportacao(e: unknown): boolean {
    if (!(e instanceof ErroApi) || e.status !== 409) return false;
    const msg = (e.corpo as { erro?: unknown } | null)?.erro;
    return typeof msg === "string" && msg.includes("Já existe importação");
  }

  async function enviarCsvPonto(arquivo: File, sobrescrever = false) {
    const base64 = await new Promise<string>((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onload = () => resolve(String(leitor.result).split(",")[1]!);
      leitor.onerror = reject;
      leitor.readAsDataURL(arquivo);
    });
    try {
      setErro(null);
      setOk("");
      await api.post(`/competencias/${id}/ponto/importar`, {
        tipo: fTipoCsv,
        nomeArquivo: arquivo.name,
        conteudoBase64: base64,
        ...(sobrescrever ? { sobrescrever: true } : {}),
      });
      const msg =
        `CSV de ponto importado (${ROTULO_TIPO_CSV_PONTO[fTipoCsv]}).` +
        (sobrescrever ? " A importação anterior foi substituída — recalcule a folha no passo 3 para refletir o novo ponto." : "");
      setOk(msg);
      notificarOk(msg);
      await carregar();
      await carregarDivergencias().catch(() => undefined);
    } catch (e) {
      if (!sobrescrever && erro409JaExisteImportacao(e)) {
        setConfirmacao({
          titulo: "Sobrescrever a importação anterior?",
          consequencia:
            "O conteúdo do arquivo diverge do que já foi importado nesta competência. Confirmando, a importação " +
            "anterior é substituída por esta (os lançamentos feitos à mão são preservados) e a folha precisa ser " +
            "RECALCULADA no passo 3 para refletir o novo ponto.",
          rotuloConfirmar: "Sobrescrever e importar",
          acao: () => void enviarCsvPonto(arquivo, true),
        });
        return;
      }
      setErro(e);
      notificarErroApi(e);
    }
  }

  /** E9-API F4 — sync do ponto direto da API do Tique Taque (os efeitos são os
   *  mesmos do upload CSV: lançamentos de HE/adicional, movimentos de banco e
   *  afastamentos, com reconciliação do saldo do banco). Erros 409/422 caem no
   *  ErroApiAlert com o diagnóstico completo do backend — EXCETO o 409 "Já
   *  existe importação" (conteúdo divergente), que vira confirmação de
   *  SOBRESCRITA (21/09/2026, pedido do dono): confirmar reenvia com
   *  `{ sobrescrever: true }`. */
  async function sincronizarDoTiqueTaque(sobrescrever = false) {
    try {
      setErro(null);
      setOk("");
      setSincronizandoTt(true);
      const r = await api.post<ResultadoSyncTiqueTaque>(
        `/competencias/${id}/ponto/sincronizar`,
        sobrescrever ? { sobrescrever: true } : undefined,
      );
      const rec = r.reconciliacaoBanco
        ? ` · banco: ${r.reconciliacaoBanco.convergentes}/${r.reconciliacaoBanco.pessoas} convergente(s)` +
          (r.reconciliacaoBanco.divergentes > 0 ? ` · ${r.reconciliacaoBanco.divergentes} DIVERGENTE(S)` : "")
        : "";
      const msg =
        `Ponto sincronizado do Tique Taque${r.geradoAgora ? "" : " (sem alterações — conteúdo idêntico)"}: ` +
        `${rotuloEfeitos(r.efeitos)}${rec}.` +
        (r.alertas.length > 0 ? ` ${r.alertas.length} alerta(s) listado(s) abaixo.` : "") +
        (sobrescrever ? " A importação anterior foi substituída — recalcule a folha no passo 3 para refletir o novo ponto." : "");
      setOk(msg);
      notificarOk(msg);
      await carregar();
      await carregarDivergencias().catch(() => undefined);
    } catch (e) {
      if (!sobrescrever && erro409JaExisteImportacao(e)) {
        setConfirmacao({
          titulo: "Sobrescrever a importação anterior?",
          consequencia:
            "O conteúdo novo do Tique Taque diverge do que já foi importado nesta competência. Confirmando, a " +
            "importação anterior é substituída pelo sync novo (os lançamentos feitos à mão são preservados) e a " +
            "folha precisa ser RECALCULADA no passo 3 para refletir o novo ponto.",
          rotuloConfirmar: "Sobrescrever e sincronizar",
          acao: () => void sincronizarDoTiqueTaque(true),
        });
        return;
      }
      setErro(e);
      notificarErroApi(e);
    } finally {
      setSincronizandoTt(false);
    }
  }

  /** 05/09/2026 (pedido do dono) — sync do Convenia direto do passo 1: mesmo
   *  endpoint global do batch da página de integrações (POST → 202 imediato
   *  com o syncId; a execução segue em background, single-flight = 409 se já
   *  há um em andamento). Aqui o acompanhamento é por poll até concluir, com
   *  o resumo no toast — afastamentos/férias novos precisam chegar ANTES da
   *  importação do ponto. */
  async function sincronizarConvenia() {
    try {
      setErro(null);
      setOk("");
      setSincronizandoCv(true);
      const ini = await api.post<{ syncId: string }>("/integracoes/convenia/sync", {});
      interface SyncConveniaJson {
        status: string; // "em_andamento" | "concluido" | "concluido_com_erros" | "falha"
        erro: string | null;
        resumo: { porDominio: Record<string, { criados: number; atualizados: number; inalterados: number; erros: number }> } | null;
      }
      let fim: SyncConveniaJson | null = null;
      for (let tentativa = 0; tentativa < 90; tentativa++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const r = await api.get<{ sync: SyncConveniaJson }>(`/integracoes/convenia/syncs/${ini.syncId}`);
          if (r.sync.status !== "em_andamento") {
            fim = r.sync;
            break;
          }
        } catch {
          /* poll tolerante — tenta de novo no próximo tick */
        }
      }
      if (!fim) {
        notificarAviso("O sync do Convenia segue em andamento — acompanhe o resumo em Integrações → Convenia.");
      } else if (fim.status === "concluido") {
        const contagens = Object.entries(fim.resumo?.porDominio ?? {})
          .map(([d, c]) => `${d}: +${c.criados} ~${c.atualizados} =${c.inalterados}${c.erros ? ` !${c.erros}` : ""}`)
          .join(" · ");
        const msg = `Convenia sincronizado — ${contagens || "sem alterações"}.`;
        setOk(msg);
        notificarOk(msg);
      } else if (fim.status === "concluido_com_erros") {
        notificarAviso(
          `Sync do Convenia concluído com erros (${fim.erro ?? "ver detalhe"}) — abra Integrações → Convenia para o log por item.`,
          "Concluído com erros",
        );
      } else {
        notificarErroApi(new Error(`Sync do Convenia falhou: ${fim.erro ?? "erro desconhecido"}`));
      }
      await carregar();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    } finally {
      setSincronizandoCv(false);
    }
  }

  async function excluirImportacao(importacaoId: string) {
    setConfirmacao({
      titulo: "Excluir esta importação de ponto?",
      consequencia:
        "Os lançamentos de HE, os movimentos de banco de horas e os ajustes criados por ESTA importação " +
        "serão removidos. Lançamentos feitos à mão (incluindo reclassificações 50%→100%) são preservados. " +
        "Depois você pode importar o arquivo corrigido de novo.",
      rotuloConfirmar: "Excluir importação",
      acao: () => void executar(() => api.del(`/ponto/importacoes/${importacaoId}`), "Importação excluída com seus efeitos (lançamentos/movimentos/ajustes)."),
    });
  }

  /** M3.1 — aplica a sugestão domingo/feriado → HE 100% (ação do gestor): o
   *  backend cria o par de lançamentos manuais (−X he50 / +X he100,
   *  pontoImportacaoId = null) — a soma he50+he100 continua fechando com o
   *  total importado. */
  async function aplicarSugestaoHe100(s: SugestaoHe100) {
    const motivoTxt = s.motivo === "feriado" ? `feriado ${s.feriadoDescricao ?? ""}`.trim() : "domingo";
    setConfirmacao({
      titulo: `Mover ${fmtHhMm(s.minutos)} de HE 50% → HE 100%?`,
      consequencia:
        `${s.nome} — ${motivoTxt} ${fmtData(s.data)}. Serão criados dois lançamentos manuais ` +
        `(−${fmtHhMm(s.minutos)} em HE 50% e +${fmtHhMm(s.minutos)} em HE 100%) — o total de HE paga não muda; ` +
        `a HE 100% vale o dobro da de 50% (Súmula 146 TST). Recalcule a folha depois para o DSR refletir.`,
      rotuloConfirmar: "Aplicar reclassificação",
      acao: () =>
        void executar(
          () => api.post(`/ponto/sugestoes-he100/${s.id}/aplicar`),
          `Reclassificação aplicada: ${fmtHhMm(s.minutos)} de ${s.nome} em ${fmtData(s.data)} movida para HE 100% (lançamentos manuais).`,
        ),
    });
  }

  /** M3.1 — descarta a sugestão com justificativa obrigatória (ex.: folga
   *  compensatória informal — OJ 410). Persiste entre re-syncs do mesmo
   *  conteúdo (mesmo dia/hash). */
  async function confirmarDescarteHe100() {
    if (!descarteHe100) return;
    const s = descarteHe100.sugestao;
    await executar(async () => {
      await api.post(`/ponto/sugestoes-he100/${s.id}/descartar`, { justificativa: descarteHe100.justificativa });
      setDescarteHe100(null);
    }, `Sugestão de ${s.nome} em ${fmtData(s.data)} descartada (justificativa registrada).`);
  }

  async function adicionarDepara() {
    await executar(async () => {
      await api.post("/ponto/depara", { chaveTipo: "nome", chave: fDeparaChave, funcionarioId: fDeparaFunc });
      setFDeparaChave("");
    }, "De-para registrado.");
  }

  async function excluirDepara(deparaId: string) {
    await executar(() => api.del(`/ponto/depara/${deparaId}`), "De-para excluído.");
  }

  async function enviarRetorno(arquivo: File) {
    const base64 = await new Promise<string>((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onload = () => resolve(String(leitor.result).split(",")[1]!);
      leitor.onerror = reject;
      leitor.readAsDataURL(arquivo);
    });
    // 03/09/2026 (regra do dono): emissão NOVA zera a lista de divergências e
    // refaz do zero; re-importação do MESMO arquivo (idempotente) preserva a
    // ciência (a lista regenerada seria idêntica) — o toast distingue os dois.
    const r = await executar(
      () =>
        api.post<{ geradoAgora: boolean; reset: { divergenciasRemovidas: number } | null; conferencia: { nivelB: { divergencias: number; bloqueantesAbertas: number } } }>(
          `/competencias/${id}/retornos`,
          { nomeArquivo: arquivo.name, conteudoBase64: base64 },
        ),
      "Retorno importado e conferência executada.",
    );
    if (r) {
      if (r.geradoAgora) {
        notificarAviso(
          `Emissão nova: lista de divergências ZERADA (${r.reset?.divergenciasRemovidas ?? 0} removida(s)) e refeita do zero — ` +
            `${r.conferencia.nivelB.divergencias} divergência(s), ${r.conferencia.nivelB.bloqueantesAbertas} bloqueante(s) aberta(s).`,
        );
      } else {
        notificarAviso("Mesmo arquivo/emissão já importado — conferência re-rodada sem zerar a lista (ciências preservadas).");
      }
    }
  }

  async function verDiff() {
    if (retornos.length < 2) return;
    try {
      setErro(null);
      const d = await api.get<{ incluidos: unknown[]; removidos: unknown[]; alterados: { codigoPessoa: number; codigoEvento: number; deCentavos: number; paraCentavos: number }[] }>(
        `/retornos/${retornos[0]!.id}/diff/${retornos[retornos.length - 1]!.id}`,
      );
      setDiff(
        `Diff ${retornos[0]!.emissao} → ${retornos[retornos.length - 1]!.emissao}: ` +
          `${d.incluidos.length} incluídos, ${d.removidos.length} removidos, ${d.alterados.length} alterados` +
          (d.alterados.length > 0
            ? ` — ${d.alterados.map((a) => `p${a.codigoPessoa} ev${a.codigoEvento}: ${fmtMoeda(a.deCentavos)} → ${fmtMoeda(a.paraCentavos)}`).join("; ")}`
            : ""),
      );
    } catch (e) {
      setErro(e);
    }
  }

  async function justificar() {
    if (!justId) return;
    await executar(async () => {
      await api.post(`/divergencias/${justId}/justificar`, { justificativa: justTexto, referenciaEstudo: justRef || undefined });
      setJustId(null);
      setJustTexto("");
      setJustRef("");
    }, "Divergência justificada.");
  }

  // 04/09/2026 — proposta de justificativa da IA no chat (botão "Aplicar
  // justificativa" do card do assistente): a IA propõe, quem grava é o dono.
  // Reusa o endpoint de ciência; o `executar` já refaz o fetch das divergências.
  async function aplicarJustificativaIaChat(divergenciaId: string, justificativa: string, referenciaEstudo?: string): Promise<boolean> {
    let ok = false;
    await executar(async () => {
      await api.post(`/divergencias/${divergenciaId}/justificar`, {
        justificativa,
        ...(referenciaEstudo ? { referenciaEstudo } : {}),
      });
      ok = true;
    }, "Justificativa aplicada (proposta da IA).");
    return ok;
  }

  // Redesenho do passo 7 (05/08/2026) — ciência em LOTE por sub-grupo de falhas iguais
  async function justificarLote() {
    if (!lote) return;
    const ids = lote.itens.filter((d) => d.status === "aberta").map((d) => d.id);
    await executar(async () => {
      const r = await api.post<{ justificadas: number; puladas: number }>(`/divergencias/justificar-lote`, {
        ids,
        justificativa: loteTexto,
        referenciaEstudo: loteRef || undefined,
      });
      setLote(null);
      setLoteTexto("");
      setLoteRef("");
      await carregarDivergencias().catch(() => undefined);
      if (r.puladas > 0) notificarAviso(`${r.justificadas} justificada(s) em lote; ${r.puladas} pulada(s) (já não estavam abertas).`);
    }, `${ids.length} divergência(s) justificada(s) em lote.`);
  }

  // 02/09/2026 — ciência SUGERIDA pela IA: uma chamada justificar-lote por
  // grupo de justificativa igual; a sugestão é da IA, o clique é do dono
  function toggleSelecaoIa(divId: string) {
    setSelecionadasIa((atual) => {
      const prox = new Set(atual);
      if (prox.has(divId)) prox.delete(divId);
      else prox.add(divId);
      return prox;
    });
  }

  async function cienciaLoteIa(lotes: LoteCienciaIa[]) {
    if (lotes.length === 0) return;
    await executar(async () => {
      let justificadas = 0;
      let puladas = 0;
      for (const l of lotes) {
        const r = await api.post<{ justificadas: number; puladas: number }>(`/divergencias/justificar-lote`, {
          ids: l.ids,
          justificativa: l.justificativa,
          referenciaEstudo: "sugestão da IA (ciência confirmada pelo dono)",
        });
        justificadas += r.justificadas;
        puladas += r.puladas;
      }
      setSelecionadasIa(new Set());
      await carregarDivergencias().catch(() => undefined);
      if (puladas > 0) notificarAviso(`${justificadas} justificada(s) com a sugestão da IA; ${puladas} pulada(s) (já não estavam abertas).`);
    }, `Ciência registrada com a justificativa sugerida pela IA (${lotes.reduce((a, l) => a + l.ids.length, 0)} divergência(s)).`);
  }

  if (!detalhe) {
    return (
      <>
        <Title order={2}>Competência {fmtCompetencia(id)}</Title>
        <ErroApiAlert erro={erro} />
        <Text c="dimmed">Carregando…</Text>
      </>
    );
  }

  const bloqueantesAbertas = divergencias.filter((d) => d.severidade === "bloqueante" && d.status === "aberta").length;
  // Redesenho do passo 7 (05/08/2026): agrupamento por classe × natureza da falha
  const gruposDivergencias = agruparDivergencias(divergencias);

  return (
    <>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Group gap="sm">
          <Title order={2}>Competência {fmtCompetencia(id)}</Title>
          <Badge size="lg">{ROTULO_ESTADO[detalhe.estado] ?? detalhe.estado}</Badge>
        </Group>
        <Text size="sm" c="dimmed" ff="monospace">
          {detalhe.perfil ? `perfil ${detalhe.perfil.nome} · ` : ""}
          {detalhe.motorVersion ?? "—"}
          {detalhe.snapshot && ` · snapshot ${fmtDataHora(detalhe.snapshot.criadoEm)}`}
          {/* 03/09/2026: o badge reflete a ESCOLHA do seletor do passo 3
              (motorCalculo; default = sugerido) — se o usuário trocou a versão,
              é a troca dele que aparece aqui, não o sugerido do gate. */}
          {!detalhe.snapshot && (motorCalculo || motorSugerido) && detalhe.motorVersion !== (motorCalculo || motorSugerido) && (
            <Badge variant="light" color="sillis" ml="xs" title="Competência aberta: o próximo cálculo usa a versão escolhida no passo 3 (padrão: a elegível mais recente — regra da Tarefa C)">
              próximo cálculo: {motorCalculo || motorSugerido}
            </Badge>
          )}
        </Text>
      </Group>

      <Stepper active={passo} onStepClick={mudarPasso} size="sm" mb="md" allowNextStepsSelect>
        {PASSOS.map((label, i) => (
          <Stepper.Step key={label} label={`${i + 1}. ${label}`} />
        ))}
      </Stepper>

      <ErroApiAlert erro={erro} />
      <OkAlert>{ok}</OkAlert>
      {confirmacao && (
        <Confirmacao
          aberta
          titulo={confirmacao.titulo}
          consequencia={confirmacao.consequencia}
          rotuloConfirmar={confirmacao.rotuloConfirmar}
          onConfirmar={() => {
            confirmacao.acao();
            setConfirmacao(null);
          }}
          onCancelar={() => setConfirmacao(null)}
        />
      )}
      {nfEdit && (
        <Modal opened onClose={() => setNfEdit(null)} title={`Editar NF ${nfEdit.numero}`} centered data-testid="modal-editar-nf">
          <Text size="xs" c="dimmed" mb="sm">
            O crédito de retenção muda na hora; a conferência de divergências será refeita no próximo recálculo/reimport.
          </Text>
          <Group align="flex-end" wrap="wrap" mb="sm">
            <Select
              label="Tomador"
              data={tomadores.map((t) => ({ value: t.id, label: `${t.nome} (${(t.aliquotaPpm / 10000).toLocaleString("pt-BR")}%)` }))}
              value={eNfTom}
              onChange={(v) => setENfTom(v ?? "")}
              w={320}
            />
            <div>
              <Text size="xs" fw={600} mb={2}>Nº NF</Text>
              <input aria-label="Número da NF (edição)" value={eNfNum} onChange={(e) => setENfNum(e.target.value)} size={8} />
            </div>
            <MoedaField label="Valor da NF" valor={eNfValor} onChange={setENfValor} w={140} />
            <div>
              <Text size="xs" fw={600} mb={2}>descrição (opcional)</Text>
              <input aria-label="Descrição da NF (edição)" value={eNfDesc} onChange={(e) => setENfDesc(e.target.value)} size={24} />
            </div>
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setNfEdit(null)}>Cancelar</Button>
            <Button onClick={() => void salvarEdicaoNf()} disabled={!eNfTom || !eNfNum.trim() || eNfValor === null}>
              Salvar alterações
            </Button>
          </Group>
        </Modal>
      )}

      {/* 21/09/2026 — o modal de avisos do ponto vive FORA dos passos: a linha-
          resumo aparece no passo 1 E no passo 2, e o modal reabre de qualquer
          um dos dois (o auto-avanço do passo não pode mais "esconder" os avisos). */}
      <PontoAvisosModal
        aberto={avisosAberto}
        onFechar={() => setAvisosAberto(false)}
        avisos={avisosPonto}
        nomesFuncionarios={funcionarios.map((f) => f.nome)}
        competenciaId={id}
      />

      {/* 09/2026+ — edição da data fim do período de ponto (o início é
          derivado: dia seguinte ao fim do período anterior; somente leitura). */}
      {editCalAberto && calPonto && (
        <Modal
          opened
          onClose={() => setEditCalAberto(false)}
          title="Editar data fim do período de ponto"
          centered
          size="sm"
          data-testid="modal-editar-ponto-fim"
        >
          <Text size="sm" mb="sm">
            Início (automático): dia seguinte ao fim do período anterior — {fmtData(calPonto.pontoInicio)}
          </Text>
          <div>
            <Text size="xs" fw={600} mb={2}>
              Nova data fim
            </Text>
            <input
              aria-label="Nova data fim do período de ponto"
              type="date"
              value={editPontoFim}
              onChange={(e) => setEditPontoFim(e.target.value)}
            />
          </div>
          <Alert color="yellow" variant="light" mt="sm" icon={<IconAlertTriangle size={16} />}>
            <Text size="xs">
              Se o dia do fim mudar, ajuste o dia de corte no Tique Taque Admin para o mesmo dia — senão a sincronização bloqueia.
            </Text>
            <Text size="xs" mt={4}>
              Se já houver ponto importado, será preciso reimportar (a sincronização pedirá a janela nova).
            </Text>
            <Text size="xs" mt={4}>
              Se a folha já foi calculada, recalcule no passo 3.
            </Text>
          </Alert>
          <ErroApiAlert erro={editCalErro} />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setEditCalAberto(false)}>
              Cancelar
            </Button>
            <Button onClick={() => void salvarPontoFim()} loading={salvandoCal} disabled={!editPontoFim}>
              Salvar
            </Button>
          </Group>
        </Modal>
      )}

      {/* ------------------------------------------------ PASSO 1 — ponto */}
      {passo === 0 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb={4}>
            1. Importe o ponto (Tique Taque) — período {calPonto ? `${fmtData(calPonto.pontoInicio)} a ${fmtData(calPonto.pontoFim)}` : "—"}
            {calPontoEditavel && podeLancar && calPonto && (
              <ActionIcon
                variant="subtle"
                size="sm"
                ml="xs"
                aria-label="Editar data fim do período de ponto"
                title="Editar data fim do período de ponto"
                onClick={abrirEdicaoPontoFim}
              >
                <IconPencil size={16} />
              </ActionIcon>
            )}
          </Title>
          <Text size="sm" c="dimmed" mb="sm">
            {calPontoEditavel
              ? `A competência ${fmtCompetencia(id)} apura o ponto do período acima (não o mês civil). A data fim é editável; o início é sempre o dia seguinte ao fim do período anterior.`
              : `A competência ${fmtCompetencia(id)} apura o ponto do período acima (24→23, não o mês civil — regra fixa 24→23 para meses anteriores a 09/2026).`}{" "}
            A importação valida o período do arquivo e recusa divergências.
          </Text>
          {podeLancar ? (
            <>
              <Group align="flex-end" mb="sm">
                <Select
                  label="Tipo do CSV"
                  value={fTipoCsv}
                  onChange={(v) => setFTipoCsv(v ?? "horas_extras")}
                  data={Object.entries(ROTULO_TIPO_CSV_PONTO).map(([value, label]) => ({ value, label }))}
                />
                <Dropzone
                  onDrop={(arquivos) => arquivos[0] && void enviarCsvPonto(arquivos[0])}
                  accept={["text/csv"]}
                  maxFiles={1}
                  style={{ flex: 1 }}
                >
                  <Group justify="center" gap="sm" mih={60} style={{ pointerEvents: "none" }}>
                    <IconFileUpload size={24} />
                    <div>
                      <Text size="sm">Arraste o CSV aqui, ou clique para escolher — {ROTULO_TIPO_CSV_PONTO[fTipoCsv]}</Text>
                      {/* 21/09/2026 — o CSV é o fluxo ALTERNATIVO (fora do wizard
                          Convenia → Tique Taque): exportado à mão do Tique Taque. */}
                      <Text size="xs" c="dimmed">fluxo alternativo: CSV exportado à mão do Tique Taque (não depende dos syncs)</Text>
                    </div>
                  </Group>
                </Dropzone>
                <div>
                  <Text size="xs" fw={600} mb={2}>
                    ou direto das APIs
                  </Text>
                  {/* 03/09/2026 (pedido do dono) — os dois syncs lado a lado na
                      mesma linha: Tique Taque (ponto) e Convenia (cadastro/
                      afastamentos — rodar ANTES de importar o ponto).
                      21/09/2026 (pedido do dono) — wizard numerado: o ponto (2º)
                      só libera depois do Convenia (1º) ter rodado nesta
                      competência; com os dois feitos (ou o ponto já importado)
                      os dois botões ficam livres (re-sync à vontade). */}
                  <Group gap="lg" wrap="nowrap" align="flex-start">
                    <div>
                      <Text size="xs" fw={600} mb={2}>
                        1º — Sincronizar Convenia (cadastro, afastamentos e férias)
                      </Text>
                      <Button
                        variant="light"
                        leftSection={<IconRefresh size={16} />}
                        onClick={() => void sincronizarConvenia()}
                        loading={sincronizandoCv}
                        data-testid="sync-convenia-passo1"
                      >
                        Sincronizar Convenia
                      </Button>
                    </div>
                    <div>
                      <Text size="xs" fw={600} mb={2}>
                        2º — Sincronizar ponto (Tique Taque)
                      </Text>
                      <Button
                        leftSection={<IconRefresh size={16} />}
                        onClick={() => void sincronizarDoTiqueTaque()}
                        loading={sincronizandoTt}
                        disabled={(statusTt !== null && !statusTt.configurado) || !pontoLiberado}
                      >
                        Sincronizar do Tique Taque
                      </Button>
                      {!pontoLiberado && (
                        <Text size="xs" c="dimmed" mt={4} maw={300}>
                          libera depois do 1º passo (o Convenia precisa rodar nesta competência antes do ponto).
                        </Text>
                      )}
                    </div>
                  </Group>
                  <Text size="xs" c="dimmed" mt={4} maw={360}>
                    o Convenia puxa afastamentos/férias novos — rode antes de importar o ponto.
                  </Text>
                </div>
              </Group>
              {statusTt !== null && !statusTt.configurado && (
                <Text size="xs" c="dimmed" mb="sm">
                  Token da API não configurado — cadastre em{" "}
                  <Anchor href="#/integracoes/tiquetaque">Integrações → Tique Taque</Anchor>. O upload CSV continua
                  valendo como contingência.
                </Text>
              )}
            </>
          ) : (
            <Text size="sm" c="dimmed" mb="sm">Importação de ponto só em competência aberta/em cálculo.</Text>
          )}
          <TabelaDados mb="sm">
            <Table.Thead>
              <Table.Tr>
                <Th>Tipo</Th>
                <Th>Origem</Th>
                <Th>Arquivo</Th>
                <Th>Período</Th>
                <Th num>Lançamentos</Th>
                <Th num>Movimentos banco</Th>
                <Th num>Ajustes</Th>
                <Th></Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {importacoesPonto.map((i) => (
                <Table.Tr key={i.id}>
                  <Table.Td>{ROTULO_TIPO_CSV_PONTO[i.tipo] ?? i.tipo}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={i.origem === "api" ? "sillis" : "gray"}>
                      {i.origem === "api" ? "api" : "csv"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">{i.nomeArquivo}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{fmtData(i.periodoInicio)} a {fmtData(i.periodoFim)}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>{i._count.lancamentos}</Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>{i._count.movimentosBanco}</Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>{i._count.ajustes}</Table.Td>
                  <Table.Td>
                    {podeLancar && (
                      <ActionIcon variant="subtle" color="red" size="sm" onClick={() => void excluirImportacao(i.id)} aria-label={`Excluir a importação ${i.nomeArquivo}`}>
                        <IconTrash size={15} />
                      </ActionIcon>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {importacoesPonto.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Text size="sm" c="dimmed">Nenhum ponto importado — sincronize do Tique Taque ou suba Horas extras / Banco de horas / Pedidos de ajustes (CSV).</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </TabelaDados>
          {/* 03/09/2026 (pedido do dono) — o mural de avisos soltos era ilegível:
              agora uma linha-resumo + modal agrupado por funcionário (com
              "Avaliar com IA" no rodapé). 21/09/2026: a mesma linha aparece no
              passo 2 também (o auto-avanço do passo escondia os avisos). */}
          {avisosPonto.length > 0 && <LinhaAvisosPonto total={avisosPonto.length} onAbrir={() => setAvisosAberto(true)} />}

          {/* M2.1 — detalhe diário do ponto (grade dia × rubricas por pessoa) */}
          {diasPonto.length > 0 && (
            <>
              <Title order={5} mt="md" mb={4}>
                Detalhe diário do ponto (HE/ad. noturno/banco por dia)
              </Title>
              <Text size="sm" c="dimmed" mb="sm">
                Detalhe auditável gravado pelo sync da API: os lançamentos seguem os TOTAIS do período — esta grade é a
                memória de qual dia gerou cada rubrica (a soma dos dias confere com o total importado; divergência vira
                alerta de integridade acima).
              </Text>
              {Object.entries(
                diasPonto.reduce<Record<string, DiaPonto[]>>((acc, d) => {
                  (acc[d.nome] ??= []).push(d);
                  return acc;
                }, {}),
              ).map(([nome, dias]) => (
                <div key={nome} style={{ marginBottom: 12 }}>
                  <Text size="sm" fw={600} mb={4}>
                    {nome}
                  </Text>
                  <TabelaDados mb="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Th>Data</Th>
                        <Th num>HE 50%</Th>
                        <Th num>HE 100%</Th>
                        <Th num>Ad. noturno</Th>
                        <Th num>Banco</Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {dias.map((d) => (
                        <Table.Tr key={d.id}>
                          <Table.Td>{fmtData(d.data)}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>{d.extra50Min !== 0 ? fmtHhMm(d.extra50Min) : "—"}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>{d.extra100Min !== 0 ? fmtHhMm(d.extra100Min) : "—"}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>{d.adicionalNoturnoMin !== 0 ? fmtHhMm(d.adicionalNoturnoMin) : "—"}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            {d.bancoMinutos !== null && d.bancoMinutos !== 0 ? fmtHhMm(d.bancoMinutos) : "—"}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </TabelaDados>
                </div>
              ))}
            </>
          )}

          {/* M3.1 — sugestões de reclassificação domingo/feriado → HE 100%
              (Súmula 146 TST; SUGESTÃO, nunca automação — precedente 4.3) */}
          {sugestoesHe100.length > 0 && (
            <>
              <Title order={5} mt="md" mb={4}>
                Domingo/feriado trabalhado → HE 100% (sugestões)
              </Title>
              <Text size="sm" c="dimmed" mb="sm">
                O Tique Taque totaliza toda hora extra paga como 50%. Os dias abaixo caíram em domingo/feriado — o
                trabalho não compensado nesses dias deve ser pago em dobro (Súmula 146 TST; a prática da empresa é HE
                100%, equivalente). <strong>A decisão é sempre do gestor</strong>: aplicar cria os lançamentos manuais
                (−HE 50% / +HE 100%, sem mudar o total de HE) e recalcular a folha reflete no DSR; descartar exige
                justificativa (ex.: folga compensatória combinada — OJ 410). O re-sync do ponto não desfaz nem recria
                decisões já tomadas; se a fonte mudar depois da aplicação, a linha é marcada como divergente (nunca
                revertida automaticamente).
              </Text>
              <TabelaDados mb="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Th>Pessoa</Th>
                    <Th>Data</Th>
                    <Th>Motivo</Th>
                    <Th num>Minutos</Th>
                    <Th>Status</Th>
                    <Th>Ações</Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sugestoesHe100.map((s) => (
                    <Table.Tr key={s.id}>
                      <Table.Td>{s.nome}</Table.Td>
                      <Table.Td>{fmtData(s.data)}</Table.Td>
                      <Table.Td>
                        {s.motivo === "feriado" ? `Feriado — ${s.feriadoDescricao ?? ""}` : "Domingo"}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>{fmtHhMm(s.minutos)}</Table.Td>
                      <Table.Td>
                        <Badge color={s.status === "pendente" ? "yellow" : s.status === "aplicada" ? "green" : "gray"} variant="light">
                          {s.status}
                        </Badge>
                        {s.divergenteFonte && (
                          <Text size="xs" c="orange.8">
                            ⚠ a fonte mudou depois da aplicação — os lançamentos manuais foram mantidos (revise)
                          </Text>
                        )}
                        {s.status === "descartada" && s.justificativa && (
                          <Text size="xs" c="dimmed">
                            {s.justificativa}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {s.status === "pendente" && podeLancar && (
                          <Group gap="xs" wrap="nowrap">
                            <Button size="xs" variant="light" onClick={() => void aplicarSugestaoHe100(s)}>
                              Aplicar he50→he100
                            </Button>
                            <Button size="xs" variant="subtle" color="gray" onClick={() => setDescarteHe100({ sugestao: s, justificativa: "" })}>
                              Descartar…
                            </Button>
                          </Group>
                        )}
                        {s.status === "pendente" && !podeLancar && (
                          <Text size="xs" c="dimmed">
                            mês sem lançamentos ({ROTULO_ESTADO[detalhe?.estado ?? ""] ?? detalhe?.estado})
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </TabelaDados>
            </>
          )}

          {/* M3.2 — fim de ciclo do banco de horas: alerta proativo no último
              mês do ciclo + relatório de fechamento de ciclo por pessoa.
              ORIENTATIVO (precedente 4.3) — a apuração do residual como
              adicional (Súmula 85 III) aguarda a decisão D7. */}
          {cicloBanco && (cicloBanco.alertasFimCiclo.length > 0 || cicloBanco.fechamentos.length > 0) && (
            <>
              <Title order={5} mt="md" mb={4}>
                Banco de horas — ciclo
              </Title>
              {cicloBanco.alertasFimCiclo.map((a) => (
                <Alert key={a.funcionarioId} color={a.motivo === "ultimo_mes" ? "orange" : "yellow"} variant="light" mb="xs">
                  <Text size="sm">{a.mensagem}</Text>
                </Alert>
              ))}
              {cicloBanco.fechamentos.length > 0 && (
                <>
                  <Text size="sm" c="dimmed" mb="sm">
                    Fechamento de ciclo por pessoa (residual da fonte no último sync × saldo derivado do ledger). Saldo
                    negativo residual é tolerado dentro do parametrizado e nunca vira ajuste automático.{" "}
                    {cicloBanco.apuracaoResidualPendenteD7}
                  </Text>
                  <TabelaDados mb="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Th>Pessoa</Th>
                        <Th>Ciclo</Th>
                        <Th>Fecha em</Th>
                        <Th num>Residual (fonte)</Th>
                        <Th num>Derivado (ledger)</Th>
                        <Th>Avaliação</Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {cicloBanco.fechamentos.map((f) => (
                        <Table.Tr key={`${f.funcionarioId}-${f.inicioIso}`}>
                          <Table.Td>{f.nome}</Table.Td>
                          <Table.Td>
                            {fmtData(f.inicioIso)} → {fmtData(f.fimIso)}
                          </Table.Td>
                          <Table.Td>{fmtCompetencia(f.competenciaFechamento)}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>{fmtHhMm(f.residualMin)}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            {f.saldoDerivadoCicloMin !== null ? fmtHhMm(f.saldoDerivadoCicloMin) : "—"}
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={
                                f.avaliacao === "negativo_revisar"
                                  ? "red"
                                  : f.avaliacao === "negativo_tolerado"
                                    ? "yellow"
                                    : f.avaliacao === "positivo"
                                      ? "blue"
                                      : "gray"
                              }
                              variant="light"
                            >
                              {f.avaliacao === "negativo_revisar"
                                ? "negativo — revisar"
                                : f.avaliacao === "negativo_tolerado"
                                  ? "negativo tolerado"
                                  : f.avaliacao === "positivo"
                                    ? "residual positivo"
                                    : "zerado"}
                            </Badge>
                            <Text size="xs" c="dimmed">
                              {f.mensagem}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </TabelaDados>
                </>
              )}
            </>
          )}

          {syncsTt.length > 0 && (
            <>
              <Group justify="space-between" mt="md" mb={4} wrap="wrap">
                <Title order={5}>Sincronizações da API nesta competência</Title>
                <Anchor href="#/integracoes/tiquetaque" size="xs">
                  configuração do token e histórico completo
                </Anchor>
              </Group>
              {/* 03/09/2026 (pedido do dono) — a tela mostra SÓ a corrida mais
                  recente: a API devolve ORDER BY criadoEm DESC (listarSyncsTiqueTaque),
                  então syncsTt[0] já é a última; o histórico completo abre em modal. */}
              <TabelaDados mb="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Th>Quando</Th>
                    <Th>Status</Th>
                    <Th num>Efeitos</Th>
                    <Th>Banco de horas</Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {syncsTt.slice(0, 1).map((s) => (
                    <LinhaSync key={s.id} s={s} />
                  ))}
                </Table.Tbody>
              </TabelaDados>
              {syncsTt.length > 1 && (
                <Button variant="subtle" size="xs" mb="sm" onClick={() => setHistoricoSyncsAberto(true)}>
                  Ver histórico completo ({syncsTt.length})
                </Button>
              )}
              <Modal
                opened={historicoSyncsAberto}
                onClose={() => setHistoricoSyncsAberto(false)}
                title={`Histórico de sincronizações da competência (${syncsTt.length})`}
                size="xl"
                centered
                data-testid="modal-historico-syncs"
              >
                <TabelaDados>
                  <Table.Thead>
                    <Table.Tr>
                      <Th>Quando</Th>
                      <Th>Status</Th>
                      <Th num>Efeitos</Th>
                      <Th>Banco de horas</Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {syncsTt.map((s) => (
                      <LinhaSync key={s.id} s={s} />
                    ))}
                  </Table.Tbody>
                </TabelaDados>
              </Modal>
              {syncsTt[0]?.resumo?.reconciliacaoBanco && (
                <BlocoReconciliacao
                  resumo={syncsTt[0].resumo.reconciliacaoBanco}
                  detalhe={syncsTt[0].resumo.reconciliacaoBancoDetalhe}
                />
              )}
            </>
          )}

          <Title order={5} mt="md" mb={4}>De-para de funcionários (nome do CSV × cadastro)</Title>
          <Text size="sm" c="dimmed" mb="sm">
            Os CSVs de banco/HE não trazem matrícula — o mapeamento por nome normalizado é obrigatório antes de importar.
          </Text>
          <TabelaDados mb="sm">
            <Table.Thead>
              <Table.Tr>
                <Th>Chave (nome do CSV, normalizado)</Th>
                <Th>Funcionário</Th>
                <Th num>Matr. Domínio</Th>
                <Th></Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {deparaPonto.map((d) => (
                <Table.Tr key={d.id}>
                  <Table.Td>
                    <Text size="xs" ff="monospace">{d.chave}</Text>
                  </Table.Td>
                  <Table.Td>{d.funcionario.nome}</Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>{d.funcionario.matriculaDominio ?? "—"}</Table.Td>
                  <Table.Td>
                    <ActionIcon variant="subtle" color="red" size="sm" onClick={() => void excluirDepara(d.id)} aria-label={`Excluir o de-para ${d.chave}`}>
                      <IconTrash size={15} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
          <Group align="flex-end">
            <div>
              <Text size="xs" fw={600} mb={2}>Nome como vem no CSV</Text>
              <input value={fDeparaChave} onChange={(e) => setFDeparaChave(e.target.value)} size={32} placeholder="ex.: ISRAEL GALVAO MESSIAS..." />
            </div>
            <Select
              label="Funcionário"
              value={fDeparaFunc}
              onChange={(v) => setFDeparaFunc(v ?? "")}
              data={funcionarios.map((f) => ({ value: f.id, label: f.nome }))}
            />
            <Button variant="light" onClick={() => void adicionarDepara()} disabled={!fDeparaChave.trim()}>
              Mapear
            </Button>
          </Group>
        </Card>
      )}

      {/* ------------------------------------------------ PASSO 2 — variáveis
          22/09/2026 (pedido do dono) — o passo é dividido em DOIS blocos
          temáticos: "Folha de ponto (Tique Taque)" (resumo por pessoa +
          espelho + pagamento do banco + form de HE/AN/dias) e "Bônus,
          comissões e abonos" (grade detalhada + form de valores). */}
      {passo === 1 && (
        <>
          <Title order={4} mb="sm">2. Lance as variáveis do mês ({lancamentos.length})</Title>

          {/* 21/09/2026 (pedido do dono) — os avisos do ponto seguem reabríveis
              aqui: o auto-avanço para o passo 2 logo após o sync escondia a
              linha do passo 1 e parecia que os avisos tinham sumido. */}
          {avisosPonto.length > 0 && <LinhaAvisosPonto total={avisosPonto.length} onAbrir={() => setAvisosAberto(true)} />}

          {/* E7b — retenção de INSS s/ NFs de serviço: lembrete SEMPRE visível
              (pedido do dono: sem NF cadastrada, a divergência retencao_credito só
              aparecia na conferência — caso real 08/2026, crédito apurado × 0 interno). */}
          <Card withBorder mb="sm" bg="gray.0" data-testid="notas-fiscais">
            <Group justify="space-between" wrap="wrap" mb={4}>
              <Title order={5}>Notas fiscais do mês (retenção de INSS)</Title>
              {retencao && (
                <Group gap="xs">
                  <Badge size="lg" variant="light">Crédito do mês: {fmtMoeda(retencao.creditoMesCentavos)}</Badge>
                  <Badge size="lg" variant="outline">Acumulado: {fmtMoeda(retencao.acumuladoCentavos)}</Badge>
                </Group>
              )}
            </Group>
            <Text size="xs" c="dimmed" mb="xs">
              NFs de serviço tomado retêm 11% de INSS — o crédito compensa a GPS e é conferido contra o retorno no passo 6.
            </Text>
            {nfs.length === 0 ? (
              <Alert color="yellow" variant="light" mb="sm" icon={<IconAlertTriangle size={16} />}>
                Nenhuma NF cadastrada nesta competência — se houve nota de serviço tomado no mês, cadastre abaixo.
              </Alert>
            ) : (
              <TabelaDados mb="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Th>Nº NF</Th>
                    <Th>Tomador</Th>
                    <Th>Descrição</Th>
                    <Th num>Valor NF</Th>
                    <Th num>Retenção</Th>
                    {podeLancar && <Th>Ações</Th>}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {nfs.map((n) => (
                    <Table.Tr key={n.id}>
                      <Table.Td><Text ff="monospace" size="sm">{n.numero}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{n.tomador.nome}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{n.descricao ?? ""}</Text></Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(n.valorCentavos)}</Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(Math.round((n.valorCentavos * n.tomador.aliquotaPpm) / 1_000_000))}</Table.Td>
                      {podeLancar && (
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <Tooltip label="Editar NF">
                              <ActionIcon variant="subtle" aria-label={`Editar NF ${n.numero}`} onClick={() => abrirEdicaoNf(n)}>
                                <IconPencil size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Excluir NF">
                              <ActionIcon variant="subtle" color="red" aria-label={`Excluir NF ${n.numero}`} onClick={() => excluirNf(n)}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </TabelaDados>
            )}
            {podeLancar && (
              <Group align="flex-end" wrap="wrap">
                {tomadores.some((t) => t.ativo) ? (
                  <Select
                    label="Tomador"
                    data={tomadores
                      .filter((t) => t.ativo) // NF nova só de tomador ativo (02/09/2026)
                      .map((t) => ({ value: t.id, label: `${t.nome} (${(t.aliquotaPpm / 10000).toLocaleString("pt-BR")}%)` }))}
                    value={fNfTom}
                    onChange={(v) => setFNfTom(v ?? "")}
                    w={320}
                  />
                ) : (
                  <Text size="sm" c="dimmed">
                    Nenhum tomador ativo para lançar NF —{" "}
                    <a href="#/tomadores">cadastre o tomador em Retenção de INSS</a>.
                  </Text>
                )}
                <div>
                  <Text size="xs" fw={600} mb={2}>Nº NF</Text>
                  <input aria-label="Número da NF" value={fNfNum} onChange={(e) => setFNfNum(e.target.value)} size={8} />
                </div>
                <MoedaField label="Valor da NF" valor={fNfValor} onChange={setFNfValor} w={140} />
                <div>
                  <Text size="xs" fw={600} mb={2}>descrição (opcional)</Text>
                  <input aria-label="Descrição da NF" value={fNfDesc} onChange={(e) => setFNfDesc(e.target.value)} size={24} />
                </div>
                <Button onClick={() => void cadastrarNf()} disabled={!fNfTom || !fNfNum.trim() || fNfValor === null}>
                  Cadastrar NF
                </Button>
              </Group>
            )}
          </Card>
          {podeLancar && sugestoes && sugestoes.sugestoes.length > 0 && (
            <>
              <Divider my="sm" />
              <Title order={5} mb={4}>Ponto de partida do mês anterior</Title>
              <Group justify="space-between" wrap="wrap">
                <Text size="sm">
                  {sugestoes.sugestoes.filter((s) => !s.jaLancado).length} variável(is) de{" "}
                  {sugestoes.origem ? fmtCompetencia(sugestoes.origem) : "—"} que ainda não estão neste mês — aplique e edite o delta
                  (a fonte oficial de HE é o CSV do ponto no passo 1).
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => void aplicarSugestoes()}
                  disabled={sugestoes.sugestoes.every((s) => s.jaLancado)}
                >
                  Aplicar sugestões
                </Button>
              </Group>
              <Text size="xs" c="dimmed" mt={4} mb="sm">
                {sugestoes.sugestoes
                  .map((s) => `${s.funcionarioNome.split(" ")[0]} ${ROTULO_TIPO_LANCAMENTO[s.tipo]} ${s.minutos !== null ? fmtHhMm(s.minutos) : fmtMoeda(s.valorCentavos)}${s.jaLancado ? " ✓" : ""}`)
                  .join(" · ")}
              </Text>
            </>
          )}

          {/* BLOCO 1 — Folha de ponto (Tique Taque): resumo por pessoa com as
              células editáveis de HE/AN/dias + as colunas somente-leitura do
              banco de horas; o detalhe diário (espelho) fica no collapse. */}
          <Card withBorder shadow="xs" mb="sm" data-testid="bloco-folha-ponto">
            <Title order={4} mb={4}>Folha de ponto (Tique Taque)</Title>
            {calPonto && (
              <Text size="sm" c="dimmed" mb="sm">
                Calendário: {calPonto.diasUteisDsr} dias úteis (base DSR) · {calPonto.diasDomingosFeriados} domingos/feriados · VT com{" "}
                {calPonto.diasUteisVt} dias úteis de consumo (crédito do mês seguinte já vem do cadastro — nada a lançar).
              </Text>
            )}

            {resumoPonto.length > 0 ? (
              <TabelaDados mb="sm" data-testid="resumo-ponto">
                <Table.Thead>
                  <Table.Tr>
                    <Th>Pessoa</Th>
                    <Th num>HE 50%</Th>
                    <Th num>HE 100%</Th>
                    <Th num>Ad. noturno</Th>
                    <Th num>Dias descontados</Th>
                    <Th num>HE paga do banco</Th>
                    <Th num>Total HE pagas</Th>
                    <Th num>Banco gerado no mês</Th>
                    <Th num>Banco consumido no mês</Th>
                    <Th num>Saldo do banco</Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {resumoPonto.map((p) => (
                    <Table.Tr key={p.funcionarioId}>
                      <Table.Td>{p.nome}</Table.Td>
                      {TIPOS_PONTO.map((t) => (
                        <Table.Td key={t} style={{ textAlign: "right" }}>
                          {p.itens[t] !== undefined && (
                            <CelulaVariavel
                              pessoaNome={p.nome}
                              itens={p.itens[t]!}
                              tipo={t}
                              podeLancar={podeLancar}
                              onSalvar={salvarInlineLancamento}
                              onExcluir={pedirExclusaoLancamento}
                              onTrocarDestino={mudarDestinoLancamento}
                            />
                          )}
                        </Table.Td>
                      ))}
                      <Table.Td style={{ textAlign: "right" }}><CelMin v={p.pagoNoMes} /></Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        <Text span size="sm" fw={600} c={p.totalPagas ? undefined : "dimmed"}>
                          {p.totalPagas ? fmtHhMm(p.totalPagas) : "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}><CelMin v={p.gerado} /></Table.Td>
                      <Table.Td style={{ textAlign: "right" }}><CelMin v={p.consumido} /></Table.Td>
                      <Table.Td
                        style={{ textAlign: "right" }}
                        title={
                          p.saldoFonte
                            ? `âncora: espelho do Tique Taque de ${fmtData(p.saldoFonte.dataFimIso)} + movimentos da folha posteriores`
                            : undefined
                        }
                      >
                        <CelMin v={p.saldo} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </TabelaDados>
            ) : (
              <Text size="sm" c="dimmed" mb="sm">Nenhum lançamento de ponto ainda — aplique as sugestões acima ou lance manualmente.</Text>
            )}

            {espelhoPonto.total > 0 && (
              <>
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  mb="sm"
                  onClick={() => setOutrasAberto((v) => !v)}
                  aria-expanded={outrasAberto}
                >
                  {outrasAberto ? "Ocultar" : "Ver"} outras rubricas do espelho ({espelhoPonto.total})
                </Anchor>
                <Collapse expanded={outrasAberto}>
                  <TabelaDados mb="sm" data-testid="outras-rubricas">
                    <Table.Thead>
                      <Table.Tr>
                        <Th>Pessoa</Th>
                        <Th num>Faltas injust. (dias)</Th>
                        <Th num>Atrasos</Th>
                        <Th num>Desconto DSR</Th>
                        <Th num>Af. não remunerado</Th>
                        <Th num>Abono</Th>
                        <Th num>Hora not. reduzida</Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {espelhoPonto.rubricas.map((r) => (
                        <Table.Tr key={r.funcionarioId}>
                          <Table.Td>{r.nome}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text span size="sm" c={diasDeFaltaJson(r.diasFaltaJson) ? undefined : "dimmed"}>
                              {diasDeFaltaJson(r.diasFaltaJson) || "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "right" }}><CelMin v={r.atrasoMin ?? 0} /></Table.Td>
                          <Table.Td style={{ textAlign: "right" }}><CelMin v={r.descontoDsrMin ?? 0} /></Table.Td>
                          <Table.Td style={{ textAlign: "right" }}><CelMin v={r.afastamentoNaoRemuneradoMin ?? 0} /></Table.Td>
                          <Table.Td style={{ textAlign: "right" }}><CelMin v={r.abonoMin ?? 0} /></Table.Td>
                          <Table.Td style={{ textAlign: "right" }}><CelMin v={r.horaNoturnaReduzidaMin ?? 0} /></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </TabelaDados>
                </Collapse>
              </>
            )}

            {/* item 3 — pagamento do banco de horas em folha (decisão do gestor no fechamento) */}
          {(() => {
            const comSaldo = saldosBanco.filter((p) => p.saldoMinutos > 0 || p.pagoNoMesMinutos > 0);
            if (comSaldo.length === 0) return null;
            return (
              <div data-testid="pagamento-banco">
                <Divider my="sm" />
                <Title order={5} mb={4}>Pagamento do banco de horas nesta folha</Title>
                <Text size="xs" c="dimmed" mb="xs">
                  As horas do banco só entram na folha quando você decide pagá-las (o que fica no banco continua no banco).
                  O pagamento entra como HE 50% no cálculo e baixa o saldo na hora.
                </Text>
                <TabelaDados>
                  <Table.Thead>
                    <Table.Tr>
                      <Th>Pessoa</Th>
                      <Th num>Saldo no banco</Th>
                      <Th num>Pago neste mês</Th>
                      {podeLancar && <Th>Pagar agora (hh:mm)</Th>}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {comSaldo.map((p) => {
                      const pagamentos = lancamentos.filter(
                        (l) => l.funcionarioId === p.funcionarioId && (l.descricao ?? "").startsWith("Pagamento em folha"),
                      );
                      return (
                        <Table.Tr key={p.funcionarioId}>
                          <Table.Td>{p.nome}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>{fmtHhMm(p.saldoMinutos)}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            {p.pagoNoMesMinutos > 0 ? fmtHhMm(p.pagoNoMesMinutos) : "—"}
                            {podeLancar &&
                              pagamentos.map((l) => (
                                <Button
                                  key={l.id}
                                  size="compact-xs"
                                  variant="subtle"
                                  color="red"
                                  ml={6}
                                  onClick={() => void estornarBanco(l.id)}
                                >
                                  estornar {fmtHhMm(l.minutos ?? 0)}
                                </Button>
                              ))}
                          </Table.Td>
                          {podeLancar && (
                            <Table.Td>
                              <Group gap={6} wrap="nowrap">
                                <input
                                  aria-label={`Pagar banco de ${p.nome}`}
                                  value={bancoMin[p.funcionarioId] ?? ""}
                                  onChange={(e) => setBancoMin((m) => ({ ...m, [p.funcionarioId]: e.target.value }))}
                                  size={6}
                                  placeholder="0:30"
                                />
                                <Button
                                  size="compact-xs"
                                  variant="light"
                                  disabled={parseHhMm(bancoMin[p.funcionarioId] || "0:00") <= 0}
                                  onClick={() => void pagarBanco(p.funcionarioId)}
                                >
                                  Pagar
                                </Button>
                              </Group>
                            </Table.Td>
                          )}
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </TabelaDados>
              </div>
            );
          })()}

          {podeLancar ? (
            <>
              <Divider my="sm" />
              <Title order={5} mb={4}>Novo lançamento</Title>
              <Group align="flex-end" wrap="wrap">
                <div style={{ minWidth: 260 }}>
                  <Select
                    label="Pessoa"
                    placeholder="Selecione a pessoa"
                    // null (não "") limpa o texto interno do Select do Mantine ao
                    // desmarcar "Incluir desligados/inativos" com um desligado escolhido
                    value={fFunc || null}
                    onChange={(v) => setFFunc(v ?? "")}
                    data={dadosSeletorPessoa(incluirDesligados)}
                    searchable
                    nothingFoundMessage="Ninguém com esse nome"
                  />
                  <Checkbox
                    mt={4}
                    size="xs"
                    label="Incluir desligados/inativos"
                    checked={incluirDesligados}
                    onChange={(e) => alternarIncluirDesligados(e.currentTarget.checked)}
                  />
                </div>
                <Select
                  label="Tipo"
                  value={fTipo}
                  onChange={(v) => setFTipo(v ?? "he50")}
                  data={TIPOS_PONTO.map((value) => ({ value, label: ROTULO_TIPO_LANCAMENTO[value] }))}
                />
                {(fTipo === "he50" || fTipo === "he100" || fTipo === "adicional_noturno") && (
                  <div>
                    <Text size="xs" fw={600} mb={2}>hh:mm</Text>
                    <input value={fMin} onChange={(e) => setFMin(e.target.value)} size={6} placeholder="1:30" />
                  </div>
                )}
                {(fTipo === "he50" || fTipo === "he100" || fTipo === "adicional_noturno") && (
                  <div>
                    <Text size="xs" fw={600} mb={2}>destino</Text>
                    <SegmentedControl
                      size="xs"
                      value={fDestino}
                      onChange={setFDestino}
                      data={[
                        { value: "folha", label: "Paga em folha" },
                        { value: "banco", label: "Banco de horas" },
                      ]}
                    />
                  </div>
                )}
                {fTipo === "dias_descontados" && (
                  <div>
                    <Text size="xs" fw={600} mb={2}>dias</Text>
                    <input value={fQtd} onChange={(e) => setFQtd(e.target.value)} size={4} />
                  </div>
                )}
                <div>
                  <Text size="xs" fw={600} mb={2}>descrição / origem (opcional)</Text>
                  <input value={fDesc} onChange={(e) => setFDesc(e.target.value)} size={24} />
                </div>
                <Button onClick={() => void enviarLancamento(1)} disabled={!fFunc}>Lançar</Button>
              </Group>
            </>
          ) : (
            <Text size="sm" c="dimmed">Lançamentos só em competência aberta/em cálculo (em retificada valem na próxima retificação).</Text>
          )}
          </Card>

          {/* BLOCO 2 — Bônus, comissões e abonos: cada item é uma linha própria
              (valor + descrição/origem), somada como valor único nos eventos
              37/9025/380 da folha; a mesma célula detalhada da grade antiga. */}
          <Card withBorder shadow="xs" mb="sm" data-testid="bloco-bonus">
            <Title order={4} mb={4}>Bônus, comissões e abonos</Title>
            <Text size="xs" c="dimmed" mb="xs">
              Cada item é uma linha própria (valor + descrição/origem): a soma vira os eventos 37/9025/380 na folha;
              o detalhe de cada item aparece na memória do espelho.
            </Text>
            {gradeBonus.linhas.length > 0 ? (
              <TabelaDados mb="sm" data-testid="grade-bonus">
                <Table.Thead>
                  <Table.Tr>
                    <Th>Pessoa</Th>
                    {gradeBonus.tipos.map((t) => (
                      <Th num key={t}>{ROTULO_TIPO_LANCAMENTO[t]}</Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {gradeBonus.linhas.map(([mat, g]) => (
                    <Table.Tr key={mat}>
                      <Table.Td>{g.nome}</Table.Td>
                      {gradeBonus.tipos.map((t) => (
                        <Table.Td key={t} style={{ textAlign: "right" }}>
                          {g.itens[t] !== undefined && (
                            <CelulaVariavel
                              pessoaNome={g.nome}
                              itens={g.itens[t]!}
                              tipo={t}
                              podeLancar={podeLancar}
                              onSalvar={salvarInlineLancamento}
                              onExcluir={pedirExclusaoLancamento}
                              onTrocarDestino={mudarDestinoLancamento}
                            />
                          )}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </TabelaDados>
            ) : (
              <Text size="sm" c="dimmed" mb="sm">Nenhuma comissão, bônus ou abono lançado ainda — lance abaixo.</Text>
            )}

            {podeLancar ? (
              <>
                <Divider my="sm" />
                <Title order={5} mb={4}>Novo lançamento</Title>
                <Group align="flex-end" wrap="wrap">
                  <div style={{ minWidth: 260 }}>
                    <Select
                      label="Pessoa"
                      placeholder="Selecione a pessoa"
                      value={fFunc2 || null}
                      onChange={(v) => setFFunc2(v ?? "")}
                      data={dadosSeletorPessoa(incluirDesligados)}
                      searchable
                      nothingFoundMessage="Ninguém com esse nome"
                    />
                    <Checkbox
                      mt={4}
                      size="xs"
                      label="Incluir desligados/inativos"
                      checked={incluirDesligados}
                      onChange={(e) => alternarIncluirDesligados(e.currentTarget.checked)}
                    />
                  </div>
                  <Select
                    label="Tipo"
                    value={fTipo2}
                    onChange={(v) => setFTipo2(v ?? "comissao")}
                    data={TIPOS_BONUS.map((value) => ({ value, label: ROTULO_TIPO_LANCAMENTO[value] }))}
                  />
                  <div>
                    <Text size="xs" fw={600} mb={2}>R$</Text>
                    <input value={fValor2} onChange={(e) => setFValor2(e.target.value)} size={9} placeholder="0,00" />
                  </div>
                  <div>
                    <Text size="xs" fw={600} mb={2}>descrição / origem (opcional)</Text>
                    <input
                      value={fDesc2}
                      onChange={(e) => setFDesc2(e.target.value)}
                      size={24}
                      title="Em comissão, bônus e abono CCT a descrição aparece no detalhe da grade e na memória do espelho — lance um item por origem"
                    />
                  </div>
                  <Button onClick={() => void enviarLancamento(2)} disabled={!fFunc2}>Lançar</Button>
                </Group>
              </>
            ) : (
              <Text size="sm" c="dimmed">Lançamentos só em competência aberta/em cálculo (em retificada valem na próxima retificação).</Text>
            )}
          </Card>
        </>
      )}

      {/* ------------------------------------------------ PASSO 3 — calcular */}
      {passo === 2 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb={4}>3. Calcule ({runs.length} execuções)</Title>
          {detalhe.estado !== "fechada" && detalhe.estado !== "retificada" ? (
            <Group mb="sm" wrap="wrap" align="flex-end">
              <Button onClick={() => confirmarCalculoFolhaMensal()}>
                Calcular folha mensal
              </Button>
              {/* 03/09/2026 (pedido do dono) — seletor da versão do cálculo ao
                  lado do Calcular: elegíveis do gate; default = sugerido (é o
                  motor que o cálculo usaria sem escolha). Lista vazia (falha no
                  gate) = seletor some e o POST vai sem motorVersion (re-pin
                  automático, comportamento de sempre). */}
              {motoresElegiveis.length > 0 && (
                <Select
                  label="Versão do cálculo"
                  w={220}
                  value={motorCalculo}
                  onChange={(v) => setMotorCalculo(v ?? "")}
                  data={motoresElegiveis.map((m) => ({ value: m, label: m }))}
                />
              )}
            </Group>
          ) : (
            <Text size="sm" c="dimmed" mb="sm">Competência fechada — novas execuções só por retificação (passo 8).</Text>
          )}

          {/* UX — "0 itens" auto-explicativo: a execução sem nenhum item quase
              sempre é cadastro incompleto (contrato vigente/matrícula). O texto
              vem do diagnóstico com as contagens reais (mesma fonte do toast do
              calcular); o botão do relatório (passo 4) explica o mesmo no tooltip. */}
          {runZeroItens && (
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} mb="sm" title="A execução calculou 0 itens">
              <Text size="sm">
                {diagElegiveis?.mensagem ??
                  "A execução calculou 0 itens. Causa provável: nenhuma pessoa com contrato vigente e ativo nesta competência " +
                    "(a projeção encontrou 0 elegíveis). Cadastre os contratos em Funcionários → aba Contrato e recalcule."}
              </Text>
              <Button size="compact-xs" variant="light" mt={6} onClick={() => navegar("/funcionarios")}>
                Abrir Funcionários
              </Button>
            </Alert>
          )}

          {/* M1.2 + M1.7 — alertas ORIENTATIVOS de férias do mês (achado B.10):
              aprovada no Convenia sem recibo → pró-rata/VT silenciosamente não
              refletem; gozo além do concessivo; férias vencidas (art. 137).
              NUNCA bloqueiam — o deep-link abre a página Férias com pessoa +
              período pré-preenchidos e a solicitação pré-selecionada no seletor
              (vínculo explícito → o alerta resolve no ato; fallback = digitação
              manual preenchida). */}
          {alertasFerias &&
            (alertasFerias.aprovadasSemRecibo.length > 0 ||
              alertasFerias.concessivoUltrapassado.length > 0 ||
              alertasFerias.vencidas.length > 0) && (
              <Alert
                color="yellow"
                variant="light"
                icon={<IconAlertTriangle size={16} />}
                mb="sm"
                title="Alertas de férias (orientativos — não bloqueiam o mês)"
              >
                {alertasFerias.aprovadasSemRecibo.map((a) => (
                  <Group key={a.solicitacaoId} justify="space-between" wrap="nowrap" py={2}>
                    <Text size="sm">{a.mensagem}</Text>
                    <Button
                      size="compact-xs"
                      variant="light"
                      onClick={() =>
                        navegar(
                          `/ferias?novo=1&func=${a.funcionarioId}&comp=${competenciaDoInicioIso(a.inicioGozo)}&inicio=${a.inicioGozo}&dias=${a.diasGozo}&sol=${a.solicitacaoId}&origem=${encodeURIComponent(`/competencias/${id}`)}`,
                        )
                      }
                    >
                      Criar recibo (seletor)
                    </Button>
                  </Group>
                ))}
                {alertasFerias.concessivoUltrapassado.map((a) => (
                  <Text key={a.solicitacaoId} size="sm" py={2}>
                    {a.mensagem}
                  </Text>
                ))}
                {alertasFerias.vencidas.length > 0 && (
                  <Text size="sm" py={2}>
                    {alertasFerias.vencidas.length} período(s) com férias VENCIDAS (art. 137):{" "}
                    {alertasFerias.vencidas.map((v) => `${v.nomeFuncionario} (limite ${fmtData(v.fimConcessivo)}, saldo ${v.saldoDerivado}d)`).join("; ")}{" "}
                    — detalhe na página Férias; a política de dobro é a dúvida D5 (o sistema só alerta).
                  </Text>
                )}
              </Alert>
            )}

          {/* M1.6 (+ M1.8) — reconciliação das 3 fontes de gozo (Convenia ×
              Tique Taque × recibo interno): divergências de CONFERÊNCIA (nivel
              FERIAS — nunca bloqueiam; o detalhe fica no passo 7) + sugestões
              de recibo PRÉ-PREENCHIDO pelas datas do ponto (deep-link abre o
              formulário manual da página Férias já preenchido). D10 aberta:
              o sistema não decide a fonte prevalente. */}
          {reconciliacaoFerias && (reconciliacaoFerias.divergencias.abertas > 0 || reconciliacaoFerias.sugestoesRecibo.length > 0) && (
            <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />} mb="sm" title="Reconciliação de férias — 3 fontes de gozo (não bloqueia o mês)">
              {reconciliacaoFerias.divergencias.abertas > 0 && (
                <Text size="sm" py={2}>
                  {reconciliacaoFerias.divergencias.abertas} divergência(s) de conferência abertas
                  {Object.entries(reconciliacaoFerias.divergencias.porClasse)
                    .map(([classe, n]) => ` · ${classe}: ${n}`)
                    .join("")}{" "}
                  — detalhe e justificativa no passo 6 (divergências).
                </Text>
              )}
              {reconciliacaoFerias.sugestoesRecibo.map((s) => (
                <Group key={`${s.funcionarioId}-${s.inicio}`} justify="space-between" wrap="nowrap" py={2}>
                  <Text size="sm">{s.mensagem}</Text>
                  <Button
                    size="compact-xs"
                    variant="light"
                    onClick={() =>
                      navegar(`/ferias?novo=1&func=${s.funcionarioId}&comp=${s.competencia}&inicio=${s.inicio}&dias=${s.dias}&origem=${encodeURIComponent(`/competencias/${id}`)}`)
                    }
                  >
                    Criar recibo pré-preenchido
                  </Button>
                </Group>
              ))}
            </Alert>
          )}

          {/* P1 — férias e 13º do mês: resumo SOMENTE-LEITURA (a configuração e o
              cálculo são nas páginas próprias); os recibos calculados/fechados
              refletem automaticamente na folha (salário pró-rata e VT) */}
          <Card withBorder mb="sm" bg="gray.0">
            <Group justify="space-between" wrap="wrap" mb={processamentos.length > 0 ? 6 : 0}>
              <Text size="sm" fw={600}>Férias e 13º do mês (processamentos)</Text>
              <Group gap="xs">
                {/* 03/09/2026 (pedido do dono) — os atalhos levam a ORIGEM para a
                    página de destino mostrar "Voltar para a competência MM/AAAA" */}
                <Button size="compact-xs" variant="light" onClick={() => navegar(`/ferias?origem=${encodeURIComponent(`/competencias/${id}`)}`)}>Abrir Férias</Button>
                <Button size="compact-xs" variant="light" onClick={() => navegar(`/decimo?origem=${encodeURIComponent(`/competencias/${id}`)}`)}>Abrir 13º</Button>
              </Group>
            </Group>
            {processamentos.length === 0 ? (
              <Text size="xs" c="dimmed">
                Nenhum processamento com pagamento neste mês — crie nas páginas Férias e 13º; os calculados refletem aqui
                na folha automaticamente.
              </Text>
            ) : (
              processamentos.map((p) => (
                <Group
                  key={p.id}
                  justify="space-between"
                  wrap="nowrap"
                  py={4}
                  style={{ cursor: "pointer", borderTop: "1px solid var(--mantine-color-gray-2)" }}
                  onClick={() =>
                    navegar(
                      // 03/09/2026 — rota por TIPO (antes: tudo que não era
                      // férias caía no 13º — a rescisão abria a página errada);
                      // todos levam ?origem= para o botão "Voltar para a
                      // competência" existir no destino
                      p.tipo === "ferias"
                        ? `/ferias/${p.id}?origem=${encodeURIComponent(`/competencias/${id}`)}`
                        : p.tipo === "rescisao"
                          ? `/rescisao?proc=${p.id}&origem=${encodeURIComponent(`/competencias/${id}`)}`
                          : `/decimo?origem=${encodeURIComponent(`/competencias/${id}`)}`,
                    )
                  }
                  role="button"
                  aria-label={`Abrir ${p.tipoRotulo}`}
                >
                  <Text size="sm">
                    <strong>{p.tipoRotulo}</strong>
                    {" — "}
                    {p.tipo === "ferias"
                      ? `${p.funcionario?.nome ?? "—"}${p.dataInicio && p.dias ? ` · ${fmtData(p.dataInicio)} (${p.dias}d)` : ""}`
                      : p.tipo === "rescisao"
                        ? (p.funcionario?.nome ?? "—")
                        : `ano ${p.ano ?? "—"}`}
                  </Text>
                  <Group gap={6} wrap="nowrap">
                    <BadgeEstadoProcessamento estado={p.estado} size="sm" />
                    {p.resumoRun && (
                      <Badge variant="light" color="sillis" size="sm">
                        {fmtMoeda(p.resumoRun.liquidoCentavos)}
                      </Badge>
                    )}
                  </Group>
                </Group>
              ))
            )}
          </Card>

          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Data</Th>
                <Th>Motor</Th>
                <Th>Cálculo</Th>
                <Th>Tipo</Th>
                <Th num>Itens calculados</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {runs.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>{fmtDataHora(r.executadoEm)}</Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">{r.motorVersion}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{ROTULO_TIPO_CALCULO[r.tipoCalculo] ?? r.tipoCalculo}</Text>
                  </Table.Td>
                  <Table.Td>{r.tipoExecucao === "retificacao" ? "retificação" : "normal"}</Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>{r._count.itens}</Table.Td>
                </Table.Tr>
              ))}
              {runs.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text size="sm" c="dimmed">Nenhuma execução ainda — use o botão Calcular.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </TabelaDados>
        </Card>
      )}

      {/* ------------------------------------------------ PASSO 4 — resultado */}
      {passo === 3 && (
        <Card withBorder shadow="xs">
          <Group justify="space-between" mb={4} wrap="wrap">
            <Title order={4}>4. Confira o resultado (última execução mensal)</Title>
            {/* UX — o botão desabilitado explica o porquê (o "relatório sumiu"
                do dono era isto: execução com 0 itens por falta de elegíveis);
                span wrapper = Tooltip funciona em botão disabled */}
            <Tooltip
              label={
                !resultadoMensal
                  ? "Habilita quando houver uma execução mensal com resultado — volte ao passo 3 e calcule."
                  : resultadoVazio
                    ? "A execução atual calculou 0 itens (nenhuma pessoa elegível) — habilita quando houver execução com resultado; veja o aviso no passo 3."
                    : "Espelho da Folha: relatório interno completo, funcionário por funcionário + sumário consolidado"
              }
              multiline
              maw={360}
              withArrow
            >
              <span>
                <Button
                  variant="light"
                  leftSection={<IconReport size={16} />}
                  onClick={() => void abrirRelatorioInterno()}
                  loading={gerandoRelatorio}
                  disabled={!resultadoMensal || resultadoVazio}
                >
                  Relatório interno completo
                </Button>
              </span>
            </Tooltip>
          </Group>
          {!resultadoMensal ? (
            <Text size="sm" c="dimmed">Sem execução mensal ainda — volte ao passo 3 e calcule.</Text>
          ) : resultadoVazio ? (
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} title="A execução calculou 0 itens">
              <Text size="sm">
                {diagElegiveis?.mensagem ??
                  "A execução atual calculou 0 itens (nenhuma pessoa elegível com contrato vigente nesta competência) — " +
                    "não há resultado para conferir aqui. Veja o aviso no passo 3, cadastre os contratos e recalcule."}
              </Text>
            </Alert>
          ) : (
            <>
              <Group gap="xl" mb="sm" wrap="wrap">
                <div>
                  <Text size="xs" c="dimmed">Proventos</Text>
                  <Text fw={700}>{fmtMoeda(resultadoMensal.totais.proventosCentavos)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Descontos</Text>
                  <Text fw={700}>{fmtMoeda(resultadoMensal.totais.descontosCentavos)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Líquido CLT</Text>
                  <Text fw={700}>{fmtMoeda(resultadoMensal.totais.liquidoCentavos)}</Text>
                </div>
                {resultadoMensal.totais.socios && (
                  <div>
                    <Text size="xs" c="dimmed">Líquido sócios</Text>
                    <Text fw={700}>{fmtMoeda(resultadoMensal.totais.socios.liquidoCentavos)}</Text>
                  </div>
                )}
                <div>
                  <Text size="xs" c="dimmed">Líquido geral</Text>
                  <Text fw={700} c="sillis.7">
                    {fmtMoeda(resultadoMensal.totais.liquidoCentavos + (resultadoMensal.totais.socios?.liquidoCentavos ?? 0))}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">FGTS</Text>
                  <Text fw={700}>{fmtMoeda(resultadoMensal.totais.fgtsCentavos)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">INSS segurados</Text>
                  <Text fw={700}>{fmtMoeda(resultadoMensal.totais.inssSeguradosCentavos + (resultadoMensal.totais.socios?.inssSeguradosCentavos ?? 0))}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">IRRF</Text>
                  <Text fw={700}>{fmtMoeda(resultadoMensal.totais.irrfCentavos)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Encargos (empresa+terceiros+RAT{resultadoMensal.totais.socios ? " + patronal CI" : ""})</Text>
                  <Text fw={700}>
                    {fmtMoeda(
                      resultadoMensal.totais.encargos.empresaCentavos +
                        resultadoMensal.totais.encargos.terceirosCentavos +
                        resultadoMensal.totais.encargos.ratFapCentavos +
                        (resultadoMensal.totais.socios?.patronalCentavos ?? 0),
                    )}
                  </Text>
                </div>
              </Group>

              <TabelaDados mb="md">
                <Table.Thead>
                  <Table.Tr>
                    <Th>Pessoa</Th>
                    <Th num>Proventos</Th>
                    <Th num>Descontos</Th>
                    <Th num>Líquido</Th>
                    <Th num>FGTS</Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {[...resultadoMensal.pessoas]
                    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
                    .map((p) => (
                      <Table.Tr key={p.codigoDominio}>
                        <Table.Td>{p.nome}</Table.Td>
                        <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(p.totais.proventosCentavos)}</Table.Td>
                        <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(p.totais.descontosCentavos)}</Table.Td>
                        <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(p.totais.liquidoCentavos)}</Table.Td>
                        <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(p.totais.fgtsCentavos)}</Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </TabelaDados>

              {(resultadoMensal.socios ?? []).length > 0 && (
                <>
                  <Title order={5} mb={4}>Sócios (pró-labore — seção CC8)</Title>
                  <TabelaDados>
                    <Table.Thead>
                      <Table.Tr>
                        <Th>Sócio</Th>
                        <Th num>Pró-labore (100)</Th>
                        <Th num>INSS CI (843)</Th>
                        <Th num>IRRF (999)</Th>
                        <Th num>Líquido</Th>
                        <Th num>Patronal CI (encargo)</Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {resultadoMensal.socios!.map((s) => {
                        const valor = (cod: number) => s.eventos.find((e) => e.codigoDominio === cod)?.valorCentavos ?? 0;
                        return (
                          <Table.Tr key={s.codigoDominio}>
                            <Table.Td>{s.nome}</Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(valor(100))}</Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(valor(843))}</Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(valor(999))}</Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(s.totais.liquidoCentavos)}</Table.Td>
                            <Table.Td style={{ textAlign: "right" }}>{fmtMoeda(s.patronalCentavos)}</Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </TabelaDados>
                </>
              )}
            </>
          )}
        </Card>
      )}

      {/* ------------------------------------------------ PASSO 5 — arquivos */}
      {passo === 4 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb={4}>5. Gere os arquivos ({artefatos.length} artefatos)</Title>
          {/* 24/09/2026 — aviso do ID Convenia com preenchimento na hora */}
          {faltandoIdConvenia.length > 0 && (
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} mb="sm" title="Falta o ID Convenia — o arquivo não casa essas pessoas" data-testid="aviso-idconvenia">
              <Text size="sm" mb="xs">
                O importador do Convenia casa cada pessoa pela coluna ID (a chave interna deles — sem ela, a importação
                responde "Nenhum registro encontrado"). Preencha e salve antes de gerar; o número fica na ficha do
                colaborador no Convenia (e nos arquivos de importação/exportação deles).
              </Text>
              <Group gap="sm" align="flex-end" wrap="wrap">
                {faltandoIdConvenia.map((f) => (
                  <TextInput
                    key={f.id}
                    label={f.nome}
                    placeholder="ID Convenia (numérico)"
                    value={idsConvenia[f.id] ?? ""}
                    onChange={(e) => setIdsConvenia((m) => ({ ...m, [f.id]: e.currentTarget.value.replace(/\D/g, "") }))}
                    w={280}
                  />
                ))}
                <Button onClick={() => void salvarIdsConvenia()} disabled={faltandoIdConvenia.every((f) => !(idsConvenia[f.id] ?? "").trim())}>
                  Salvar IDs
                </Button>
              </Group>
            </Alert>
          )}
          {/* UX — "0 itens" (regra do dono: sem relatório, sem arquivos
              externos): execução mensal sem itens desabilita os 4 exports
              (span wrapper = Tooltip em botão disabled); a API bloqueia com
              409 + diagnóstico (mesma fonte do Alert do passo 3). */}
          <Group mb="sm">
            {TIPOS_ARTEFATO.map(([tipo, rotulo]) => (
              <Tooltip
                key={tipo}
                label="A execução atual calculou 0 itens (nenhuma pessoa elegível) — nada a exportar; veja o aviso no passo 3 e recalcule."
                multiline
                maw={360}
                withArrow
                disabled={!runZeroItens}
              >
                <span>
                  <Button
                    variant="light"
                    disabled={runZeroItens}
                    onClick={() => void executar(() => api.post(`/competencias/${id}/exportar`, { tipo }), `Artefato ${rotulo} gerado.`)}
                  >
                    Gerar {rotulo}
                  </Button>
                </span>
              </Tooltip>
            ))}
          </Group>
          {runZeroItens && (
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} mb="sm" title="Nada a exportar — a execução calculou 0 itens">
              <Text size="sm">
                {diagElegiveis?.mensagem ??
                  "A execução atual não tem itens — os arquivos para os sistemas externos ficam indisponíveis enquanto " +
                    "não houver execução com resultado. Veja o aviso no passo 3, cadastre os contratos e recalcule."}
              </Text>
            </Alert>
          )}
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Tipo</Th>
                <Th>Arquivo</Th>
                <Th num>Bytes</Th>
                <Th>Gerado em</Th>
                <Th></Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {artefatos.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td>
                    <Text size="xs" ff="monospace">{a.tipo}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">{a.nomeArquivo}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>{a.tamanhoBytes}</Table.Td>
                  <Table.Td>{fmtDataHora(a.criadoEm)}</Table.Td>
                  <Table.Td>
                    <ActionIcon component="a" href={urlComToken(`/artefatos/${a.id}/download`)} download variant="subtle" aria-label={`Baixar ${a.nomeArquivo}`} size="sm">
                      <IconDownload size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
              {artefatos.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text size="sm" c="dimmed">Nenhum artefato gerado ainda — gere acima (a partir da última execução).</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </TabelaDados>
        </Card>
      )}

      {/* ------------------------------------------------ PASSO 6 — divergências (com importação do retorno)
          03/09/2026 (pedido do dono): o passo "Importar retorno" foi FUNDIDO
          aqui — a importação do TXT da Contmega mora na mesma tela das
          divergências (importar já zera a lista e refaz a conferência). */}
      {passo === 5 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb={4}>
            6. Resolva as divergências ({divergencias.length})
            {bloqueantesAbertas > 0 && (
              <Badge color="red" ml="sm">{bloqueantesAbertas} bloqueante(s) aberta(s)</Badge>
            )}
          </Title>

          {/* Importação do retorno da Contmega (615 campos) — emissão NOVA zera
              a lista abaixo e refaz a conferência do zero; mesmo arquivo =
              idempotente (preserva as ciências). */}
          <Group align="flex-end" mb="sm">
            <Dropzone onDrop={(arquivos) => arquivos[0] && void enviarRetorno(arquivos[0])} accept={[".txt", "text/plain"]} maxFiles={1} style={{ flex: 1 }}>
              <Group justify="center" gap="sm" mih={60} style={{ pointerEvents: "none" }}>
                <IconFileUpload size={24} />
                <Text size="sm">Arraste o TXT de retorno (615 campos) aqui, ou clique para escolher</Text>
              </Group>
            </Dropzone>
            {retornos.length >= 2 && (
              <Button variant="default" onClick={() => void verDiff()}>
                Diff entre emissões
              </Button>
            )}
          </Group>
          {diff && (
            <Text size="sm" ff="monospace" mb="sm">{diff}</Text>
          )}
          {retornos.length > 0 && (
            <TabelaDados mb="sm">
              <Table.Thead>
                <Table.Tr>
                  <Th>Arquivo</Th>
                  <Th>Emissão</Th>
                  <Th num>Eventos</Th>
                  <Th>Importado em</Th>
                  <Th></Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {retornos.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text size="xs" ff="monospace">{r.nomeArquivo}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">{r.emissao}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>{r._count.eventos}</Table.Td>
                    <Table.Td>{fmtDataHora(r.importadoEm)}</Table.Td>
                    <Table.Td>
                      <ActionIcon component="a" href={urlComToken(`/retornos/${r.id}/download`)} download variant="subtle" aria-label={`Baixar ${r.nomeArquivo}`} title="Baixar o TXT importado" size="sm">
                        <IconDownload size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </TabelaDados>
          )}

          <Group mb="sm" gap="xs" wrap="wrap">
            <Badge variant="light" color="gray" size="lg">{divergencias.length} divergência(s)</Badge>
            {([
              ["bloqueante", "red", divergencias.filter((d) => d.severidade === "bloqueante").length + " bloqueante(s)"],
              ["alerta", "yellow", divergencias.filter((d) => d.severidade === "alerta").length + " alerta(s)"],
              ["informativo", "gray", divergencias.filter((d) => d.severidade === "informativo").length + " informativa(s)"],
            ] as const).map(([sev, cor, rotulo]) => (
              <Badge
                key={sev}
                data-sev={sev}
                size="lg"
                color={cor}
                variant={fSev === sev ? "filled" : "light"}
                style={{ cursor: "pointer", opacity: fSev && fSev !== sev ? 0.45 : 1 }}
                onClick={() => setFSev(fSev === sev ? "" : sev)}
                title={fSev === sev ? "Limpar o filtro de severidade" : `Filtrar só ${rotulo}`}
              >
                {rotulo}
              </Badge>
            ))}
            <Badge variant="outline" color="sillis" size="lg">{gruposDivergencias.length} grupo(s)</Badge>
            <Menu shadow="md" width={280}>
              <Menu.Target>
                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}>
                  Exportar divergências
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>TODAS as divergências, sem os filtros da tela</Menu.Label>
                <Menu.Item onClick={() => exportarDivergencias("md")}>Markdown (.md) — legível</Menu.Item>
                <Menu.Item onClick={() => exportarDivergencias("json")}>JSON (.json) — para o agente de IA</Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <FileButton onChange={(f) => void escolherArquivoCorrecoes(f)} accept=".json,application/json">
              {(props) => (
                <Button
                  {...props}
                  size="xs"
                  variant="light"
                  color="sillis"
                  leftSection={<IconUpload size={14} />}
                  loading={correcoesOcupado}
                  title="Importa o JSON de correções devolvido pelo agente de IA (dry-run com preview antes de aplicar)"
                >
                  Importar correções
                </Button>
              )}
            </FileButton>
            <Select
              placeholder="todos os status"
              clearable
              value={fStatus || null}
              onChange={(v) => setFStatus(v ?? "")}
              data={[
                { value: "aberta", label: "aberta" },
                { value: "justificada", label: "justificada (humana)" },
                { value: "autojustificada", label: "auto-justificada (sistema)" },
                { value: "resolvida", label: "resolvida" },
              ]}
            />
            {fStatus === "aberta" && (
              <Text size="xs" c="dimmed">
                exibindo só as abertas — limpe o filtro de status para ver o histórico (justificadas/resolvidas)
              </Text>
            )}
          </Group>
          {/* Camada de IA (03/09/2026; evolução 02/09/2026) — análise das
              divergências; a IA propõe, nunca aplica. A análise mais recente
              ENRIQUECE os cards abaixo (faixa "Avaliação da IA" por id). */}
          <SecaoIaPasso7
            competenciaId={id}
            totalDivergencias={divergencias.length}
            onPrepararCorrecao={(arq, nome) => void carregarCorrecoesIa(arq, nome)}
            onAnaliseCarregada={setAnaliseIa}
            onAplicarJustificativa={(divId, just, ref) => aplicarJustificativaIaChat(divId, just, ref)}
          />
          {/* E7b — divergência de retenção sem NF cadastrada: aponta o passo 2
              (a correção é cadastrar as NFs do mês e reimportar/reconferir o retorno). */}
          {divergencias.some((d) => d.alvo.startsWith("retencao_credito") && d.status === "aberta") && (
            <Alert color="yellow" variant="light" mb="sm" icon={<IconAlertTriangle size={16} />} data-testid="alerta-retencao-nf">
              {nfs.length === 0
                ? "Há crédito de retenção apurado no retorno e nenhuma NF cadastrada — cadastre no passo 2 e reconfile."
                : "O crédito de retenção interno difere do apurado no retorno — confira as NFs do mês no passo 2 e reconfile."}
            </Alert>
          )}
          {divergencias.length === 0 ? (
            <Text size="sm" c="dimmed">Nenhuma divergência — importe o retorno da Contmega aqui em cima para rodar a conferência.</Text>
          ) : (
            <SecaoDivergencias
              grupos={gruposDivergencias}
              analiseIa={analiseIa}
              selecionadasIa={selecionadasIa}
              onToggleSelecaoIa={toggleSelecaoIa}
              onCienciaLoteIa={(lotes) => void cienciaLoteIa(lotes)}
              onDarCiencia={(d) => {
                setJustId(d.id);
                setJustTexto(d.justificativa ?? "");
                setJustRef(d.referenciaEstudo ?? "");
              }}
              onLote={(sg) => { setLote(sg); setLoteTexto(""); setLoteRef(""); }}
              onPrepararCorrecao={(arq, nome) => void carregarCorrecoesIa(arq, nome)}
            />
          )}
          {/* 02/09/2026 — os cards das divergências (SecaoDivergencias acima)
              trazem o comparativo Nosso × Domínio, o contexto legível, a
              avaliação da IA inline e a ciência sugerida pela IA (clique humano) */}
        </Card>
      )}

      {/* ------------------------------------------------ PASSO 7 — fechar */}
      {passo === 6 && (
        <Card withBorder shadow="xs">
          <Title order={4} mb={4}>7. Confira e feche a competência</Title>
          <Group gap="xl" mb="md" wrap="wrap">
            <div>
              <Text size="xs" c="dimmed">Estado</Text>
              <Badge size="lg">{ROTULO_ESTADO[detalhe.estado] ?? detalhe.estado}</Badge>
            </div>
            <div>
              <Text size="xs" c="dimmed">Execuções</Text>
              <Text fw={700}>{runs.length}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Artefatos</Text>
              <Text fw={700}>{artefatos.length}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Retornos</Text>
              <Text fw={700}>{retornos.length}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Bloqueantes abertas</Text>
              <Text fw={700} c={bloqueantesAbertas > 0 ? "red.7" : "sillis.7"}>{bloqueantesAbertas}</Text>
            </div>
            {detalhe.snapshot && (
              <div>
                <Text size="xs" c="dimmed">Snapshot</Text>
                <Text size="sm" fw={600}>gravado em {fmtDataHora(detalhe.snapshot.criadoEm)}</Text>
              </div>
            )}
          </Group>
          <Text size="sm" c="dimmed" mb="md">
            O ciclo: conferir exige retorno importado e zero bloqueantes abertas; fechar trava a competência (correções só por
            retificação — a versão original fica intacta como snapshot imutável).
            {detalhe.estado === "em_calculo" && " O Fechar já confere automaticamente antes de pedir a confirmação."}
          </Text>
          <Group>
            {detalhe.estado !== "fechada" && detalhe.estado !== "retificada" && (
              <>
                <Button variant="light" onClick={() => void executar(() => api.post(`/competencias/${id}/conferir`), "Competência conferida — sem bloqueantes abertas.")}>
                  Conferir
                </Button>
                <Button onClick={() => void fecharComConferencia()}>
                  Fechar competência
                </Button>
              </>
            )}
            {(detalhe.estado === "fechada" || detalhe.estado === "retificada") && (
              <Button variant="light" onClick={() => void abrirRetificar()}>
                Retificar
              </Button>
            )}
          </Group>
          {/* R3 — zona de perigo: cancelar = apagar a geração do mês (só não fechadas) */}
          {detalhe.estado !== "fechada" && detalhe.estado !== "retificada" && (
            <>
              <Divider my="lg" label="Zona de perigo" labelPosition="left" />
              <Group justify="space-between" wrap="wrap">
                <Text size="sm" c="dimmed" maw={520}>
                  Cancelar apaga a competência e tudo que foi gerado nela (lançamentos, cálculos, arquivos, retorno importado).
                  Depois dá para abrir o mesmo mês de novo — inclusive com outro motor. Mês fechado não pode ser cancelado.
                </Text>
                <Button variant="subtle" color="red" onClick={() => void abrirCancelar()}>
                  Cancelar competência…
                </Button>
              </Group>
            </>
          )}
        </Card>
      )}

      <Group justify="space-between" mt="md">
        <Button variant="default" disabled={passo === 0} onClick={() => mudarPasso((p) => Math.max(0, p - 1))}>
          ← Voltar
        </Button>
        <Button variant="default" disabled={passo >= PASSOS.length - 1} onClick={() => mudarPasso((p) => Math.min(PASSOS.length - 1, p + 1))}>
          Avançar →
        </Button>
      </Group>

      {/* R1 — visualizador do Espelho da Folha (iframe do artefato HTML) */}
      <Modal
        opened={relatorioAberto !== null}
        onClose={() => setRelatorioAberto(null)}
        title={`Espelho da Folha — ${relatorioAberto?.nomeArquivo ?? ""}`}
        size="90%"
        centered
      >
        {relatorioAberto && (
          <>
            <Group justify="flex-end" mb="sm">
              <Button
                variant="light"
                leftSection={<IconPrinter size={16} />}
                onClick={() => {
                  const frame = document.getElementById("iframe-espelho") as HTMLIFrameElement | null;
                  frame?.contentWindow?.print();
                }}
              >
                Imprimir
              </Button>
              <Button
                component="a"
                href={urlComToken(`/artefatos/${relatorioAberto.artefatoId}/download`)}
                download={relatorioAberto.nomeArquivo}
                leftSection={<IconDownload size={16} />}
              >
                Baixar HTML
              </Button>
            </Group>
            <iframe
              id="iframe-espelho"
              src={urlComToken(`/artefatos/${relatorioAberto.artefatoId}/download`)}
              title="Espelho da Folha"
              style={{ width: "100%", height: "70vh", border: "1px solid var(--mantine-color-gray-3)", borderRadius: 8 }}
            />
          </>
        )}
      </Modal>

      {/* justificativa inline (modal) */}
      {/* Redesenho do passo 7 (05/08/2026) — ciência em LOTE do sub-grupo */}
      <Modal
        opened={lote !== null}
        onClose={() => setLote(null)}
        title={lote ? `Dar ciência em lote — ${lote.rotulo}` : "Ciência em lote"}
        centered
      >
        {lote && (
          <>
            <Text size="sm" mb="xs">
              {lote.itens.filter((d) => d.status === "aberta").length} divergência(s) aberta(s) de{" "}
              <strong>{lote.rotulo}</strong> receberão a mesma justificativa (uma transação; trilha por item; o que
              já não estiver aberto é pulado, nunca reaberto):
            </Text>
            <Text size="xs" c="dimmed" mb="xs" style={{ maxHeight: 90, overflow: "auto" }}>
              {lote.itens.filter((d) => d.status === "aberta").map((d) => d.descricao).join("\n")}
            </Text>
            <Textarea rows={3} value={loteTexto} onChange={(e) => setLoteTexto(e.currentTarget.value)} placeholder="Justificativa (obrigatória)" mb="sm" />
            <Group align="flex-end">
              <div style={{ flex: 1 }}>
                <Text size="xs" fw={600} mb={2}>referência de estudo (opcional)</Text>
                <input value={loteRef} onChange={(e) => setLoteRef(e.target.value)} size={40} />
              </div>
              <Button onClick={() => void justificarLote()} disabled={!loteTexto.trim()}>
                Justificar {lote.itens.filter((d) => d.status === "aberta").length} em lote
              </Button>
            </Group>
          </>
        )}
      </Modal>

      {/* Import de correções (01/09/2026) — dry-run primeiro; "Aplicar" é a 2ª chamada */}
      <Modal
        opened={correcoesRel !== null}
        onClose={fecharCorrecoes}
        title={correcoesRel?.aplicar ? "Resultado do import de correções" : "Prévia do import de correções (nada aplicado ainda)"}
        centered
        size="lg"
      >
        {correcoesRel && (
          <>
            <Text size="sm" mb="xs">
              Arquivo <strong>{correcoesArqNome}</strong>
              {correcoesRel.descricao ? ` — ${correcoesRel.descricao}` : ""}
            </Text>
            <Group gap="xs" mb="sm" wrap="wrap">
              <Badge variant="light" color="gray" size="lg">{correcoesRel.totais.total} ação(ões)</Badge>
              <Badge color={correcoesRel.aplicar ? "green" : "sillis"} size="lg">
                {correcoesRel.totais.validas} {correcoesRel.aplicar ? "aplicada(s)" : "válida(s)"}
              </Badge>
              {correcoesRel.totais.puladas > 0 && <Badge color="gray" size="lg">{correcoesRel.totais.puladas} pulada(s)</Badge>}
              {correcoesRel.totais.falhas > 0 && <Badge color="red" size="lg">{correcoesRel.totais.falhas} falha(s)</Badge>}
              {correcoesRel.totais.naoSuportadas > 0 && <Badge color="yellow" size="lg">{correcoesRel.totais.naoSuportadas} não suportada(s)</Badge>}
              {correcoesRel.totais.requerRecalculo > 0 && (
                <Badge variant="outline" color="orange" size="lg">{correcoesRel.totais.requerRecalculo} pede(m) recálculo</Badge>
              )}
            </Group>
            {correcoesRel.acoes.map((a) => (
              <Group key={a.indice} align="flex-start" wrap="nowrap" gap="sm" py={6} style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
                <Badge data-status-acao={a.status} color={COR_STATUS_ACAO[a.status] ?? "gray"} variant={a.status === "falhou" ? "filled" : "light"} mt={2}>
                  {ROTULO_STATUS_ACAO[a.status] ?? a.status}
                </Badge>
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600}>{ROTULO_TIPO_ACAO[a.tipo] ?? a.tipo}</Text>
                  <Text size="xs" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{a.detalhe}</Text>
                </div>
              </Group>
            ))}
            {!correcoesRel.aplicar && (
              <Text size="xs" c="dimmed" mt="sm">
                Ações com falha ou não suportadas NUNCA são aplicadas — ajuste o arquivo com o agente e importe de novo.
              </Text>
            )}
            {correcoesRel.aplicar && correcoesRel.totais.requerRecalculo > 0 && (
              <Alert color="orange" variant="light" mt="sm" icon={<IconAlertTriangle size={16} />}>
                Variáveis mudaram — recalcule a competência no passo 3 (e os recibos de férias afetados) para a conferência refletir as correções.
              </Alert>
            )}
            <Group justify="flex-end" mt="md">
              {!correcoesRel.aplicar && (
                <Button onClick={() => void aplicarCorrecoes()} loading={correcoesOcupado} disabled={correcoesRel.totais.validas === 0}>
                  Aplicar {correcoesRel.totais.validas} ação(ões) válida(s)
                </Button>
              )}
              <Button variant="default" onClick={fecharCorrecoes}>{correcoesRel.aplicar ? "Fechar" : "Cancelar"}</Button>
            </Group>
          </>
        )}
      </Modal>

      <Modal opened={justId !== null} onClose={() => setJustId(null)} title="Justificar divergência" centered>
        <Text size="sm" mb="xs">Explique por que esta divergência é aceitável (fica registrada na trilha):</Text>
        <textarea rows={3} value={justTexto} onChange={(e) => setJustTexto(e.target.value)} placeholder="Justificativa (obrigatória)" style={{ width: "100%", marginBottom: 8 }} />
        <Group align="flex-end">
          <div style={{ flex: 1 }}>
            <Text size="xs" fw={600} mb={2}>referência de estudo (opcional)</Text>
            <input value={justRef} onChange={(e) => setJustRef(e.target.value)} size={40} />
          </div>
          <Button onClick={() => void justificar()} disabled={!justTexto.trim()}>
            Gravar justificativa
          </Button>
        </Group>
      </Modal>

      {/* M3.1 — descarte da sugestão domingo/feriado → HE 100% (justificativa obrigatória) */}
      <Modal
        opened={descarteHe100 !== null}
        onClose={() => setDescarteHe100(null)}
        title={descarteHe100 ? `Descartar sugestão de ${descarteHe100.sugestao.nome}?` : "Descartar sugestão?"}
        centered
      >
        {descarteHe100 && (
          <>
            <Text size="sm" mb="xs">
              {fmtHhMm(descarteHe100.sugestao.minutos)} em {fmtData(descarteHe100.sugestao.data)} (
              {descarteHe100.sugestao.motivo === "feriado"
                ? `feriado ${descarteHe100.sugestao.feriadoDescricao ?? ""}`
                : "domingo"}
              ) NÃO serão movidos para HE 100%. Registre o motivo — ex.: folga compensatória combinada (OJ 410:
              folga em até 7 dias não é rastreável no ponto). O descarte fica auditado e o re-sync do ponto não
              recria a sugestão enquanto a fonte não mudar.
            </Text>
            <Textarea
              rows={3}
              value={descarteHe100.justificativa}
              onChange={(e) => setDescarteHe100({ ...descarteHe100, justificativa: e.currentTarget.value })}
              placeholder="Justificativa (obrigatória)"
              mb="sm"
            />
            <Group justify="flex-end">
              <Button variant="subtle" color="gray" onClick={() => setDescarteHe100(null)}>
                Voltar
              </Button>
              <Button color="yellow" onClick={() => void confirmarDescarteHe100()} disabled={!descarteHe100.justificativa.trim()}>
                Descartar sugestão
              </Button>
            </Group>
          </>
        )}
      </Modal>

      {/* R3 — cancelamento da competência (zona de perigo, com o resumo real) */}
      <Modal opened={cancelarAberto} onClose={() => setCancelarAberto(false)} title={`Cancelar a competência ${fmtCompetencia(id)}?`} centered size="lg">
        {resumoCancel && (
          <>
            <Text size="sm" mb="sm">
              Isto <strong>apaga a competência inteira</strong> — o mês deixa de existir e pode ser aberto de novo depois
              (até com outro motor). O que será apagado agora:
            </Text>
            <Table withRowBorders={false} verticalSpacing={4} mb="sm">
              <Table.Tbody>
                {(
                  [
                    ["Lançamentos do mês", resumoCancel.contagens.lancamentos],
                    ["Execuções (cálculos)", resumoCancel.contagens.runs],
                    ["Itens calculados", resumoCancel.contagens.itensCalculados],
                    ["Arquivos gerados", resumoCancel.contagens.artefatos],
                    ["Retornos da Contmega importados", resumoCancel.contagens.retornos],
                    ["Divergências da conferência", resumoCancel.contagens.divergencias],
                    ["Importações de ponto", resumoCancel.contagens.pontoImportacoes],
                    ["Ajustes de ponto", resumoCancel.contagens.pontoAjustes],
                    ["Movimentos de banco de horas", resumoCancel.contagens.movimentosBancoHoras],
                    ["Notas fiscais de retenção", resumoCancel.contagens.notasFiscais],
                    ["Análises de IA", resumoCancel.contagens.analisesIa ?? 0],
                    ["Mensagens do chat de IA", resumoCancel.contagens.mensagensIa ?? 0],
                    ["Anexos do chat de IA", resumoCancel.contagens.anexosIa ?? 0],
                  ] as [string, number][]
                )
                  .filter(([, n]) => n > 0)
                  .map(([rotulo, n]) => (
                    <Table.Tr key={rotulo}>
                      <Table.Td><Text size="sm">{rotulo}</Text></Table.Td>
                      <Table.Td style={{ textAlign: "right" }}><Text size="sm" fw={700}>{n}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                {Object.values(resumoCancel.contagens).every((n) => n === 0) && (
                  <Table.Tr><Table.Td><Text size="sm" c="dimmed">Nada gerado ainda — só a abertura será removida.</Text></Table.Td></Table.Tr>
                )}
              </Table.Tbody>
            </Table>
            {resumoCancel.artefatos.length > 0 && (
              <Text size="sm" mb="sm">
                Arquivos que existiam: <strong>{resumoCancel.artefatos.map((a) => a.nomeArquivo).join(", ")}</strong>
              </Text>
            )}
            {resumoCancel.contagens.emprestimosAReativar > 0 && (
              <Text size="sm" mb="sm">
                {resumoCancel.contagens.emprestimosAReativar} empréstimo(s) voltam a ficar ativos (a última parcela era deste mês):
                {" "}{resumoCancel.emprestimos.map((e) => e.descricao).join(", ")}.
              </Text>
            )}
            {(resumoCancel.contagens.artefatos > 0 || resumoCancel.contagens.retornos > 0) && (
              <Text size="sm" c="red.7" fw={600} mb="sm">
                Se algum arquivo já foi enviado à Contmega (ou usado fora do sistema), registre isso no motivo — o sistema
                apaga daqui, mas não desfaz o que já saiu da empresa.
              </Text>
            )}
            <Textarea
              label="Motivo do cancelamento (obrigatório — fica na trilha de auditoria)"
              placeholder="Ex.: aberto com o motor errado; vou reabrir com o motor@2026.2"
              value={motivoCancel}
              onChange={(e) => setMotivoCancel(e.currentTarget.value)}
              minRows={2}
              mb="md"
              required
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCancelarAberto(false)}>
                Voltar
              </Button>
              <Button color="red" loading={cancelando} disabled={!motivoCancel.trim()} onClick={() => void confirmarCancelar()}>
                Cancelar e apagar tudo
              </Button>
            </Group>
          </>
        )}
      </Modal>

      {/* R3 — retificação com escolha de motor */}
      <Modal opened={retificarAberto} onClose={() => setRetificarAberto(false)} title={`Retificar a competência ${fmtCompetencia(id)}?`} centered>
        <Text size="sm" mb="md">
          Uma nova execução marcada como retificação será gravada com os dados atuais. A execução original NÃO é alterada —
          o passado não se corrige, se retifica. Lançamentos feitos agora só valem na próxima retificação.
        </Text>
        {gateRet && gateRet.elegiveis.length > 0 && (
          <Select
            label="Motor desta retificação"
            description={
              motorRet !== (detalhe?.motorVersion ?? "motor@2026.1")
                ? "Trocando o motor: as próximas retificações passam a usar esta versão por padrão (o fechamento original fica intacto)."
                : "Por padrão, a mesma versão do fechamento."
            }
            value={motorRet}
            onChange={(v) => setMotorRet(v ?? "")}
            data={gateRet.elegiveis.map((m) => ({
              value: m,
              label: `${m}${m === (detalhe?.motorVersion ?? "motor@2026.1") ? " (atual)" : ""}`,
            }))}
            mb="sm"
          />
        )}
        <Textarea
          label={
            motorRet !== (detalhe?.motorVersion ?? "motor@2026.1")
              ? "Motivo da troca de motor (obrigatório — fica na trilha)"
              : "Motivo (opcional — fica na trilha)"
          }
          placeholder="Ex.: benefício novo precisa entrar no cálculo — retifico com o motor@2026.2"
          value={motivoRet}
          onChange={(e) => setMotivoRet(e.currentTarget.value)}
          minRows={2}
          mb="md"
          required={motorRet !== (detalhe?.motorVersion ?? "motor@2026.1")}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setRetificarAberto(false)}>
            Voltar
          </Button>
          <Button
            onClick={() => void confirmarRetificar()}
            disabled={!motorRet || (motorRet !== (detalhe?.motorVersion ?? "motor@2026.1") && !motivoRet.trim())}
          >
            Retificar
          </Button>
        </Group>
      </Modal>
    </>
  );
}
